<template>
  <div
    ref="datatableRef"
    :class="[
      'datatable-v3 rounded-lg bg-white',
      {
        scrollable,
        'fixed-right-column': hasActionsColumn && !responsiveActive,
        'shadow-left': state.shadows.left,
        'shadow-right': state.shadows.right,
        'shadow-top': state.shadows.top,
        'shadow-bottom': state.shadows.bottom,
        'no-data': state.resultCount === 0
      }
    ]"
  >
    <header
      v-if="!hideHeader"
      ref="header"
      class="flex gap-2.5 items-center pb-4 w-full font-medium"
    >
      <div
        class="flex flex-wrap flex-1 px-6 shrink md:gap-4 justify-between items-start self-stretch my-auto w-full basis-0 min-w-0 max-md:max-w-full"
      >
        <h2
          class="page-header-1 datatable-title mr-3 mb-3 mt-2 md:mr-0 md:mb-0"
          :class="{ 'page-header-2': subpage }"
          :data-cy="title"
        >
          {{ title }}
        </h2>
        <div
          class="flex gap-4 items-end self-stretch my-auto text-sm flex-wrap"
        >
          <template v-if="!hideGlobalFilters">
            <DatatableFilter
              v-for="filter in globalFilters"
              :key="filter.name"
              :filter="filter"
              :data-cy="`${filter.name}_column_filter`"
              class="toggle-column-filter"
              :class="{ 'mb-3': filter.type === 'toggle' }"
              :version="2"
              :datatable-id="id"
              @refresh-table="refreshData"
              @filter-applied="emit('filter-applied', $event)"
              @filter-removed="emit('filter-removed', $event)"
            />
          </template>
          <button
            v-if="!hideFilters"
            id="datatable-filter-button"
            class="flex relative gap-2 justify-center items-center p-4 md:px-5 md:py-2.5 text-gray-900 whitespace-nowrap bg-white rounded-full md:rounded-lg border border-solid"
            data-cy="datatableFilterBtn"
            @click="toggleFiltersOpen"
          >
            <CloseIcon v-if="filterButtonOpen" height="12" />
            <SearchIcon v-else class="h-3" />
            <span class="hidden md:block">Filter</span>

            <div
              v-if="appliedFilterCount > 0"
              :class="[
                'inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-900 rounded-full',
                {
                  'absolute rounded-full -top-2 -end-2': breakpoints().sm
                }
              ]"
            >
              {{ appliedFilterCount }}
            </div>
          </button>
          <!--
            hideFilters is not a condition on this trigger, and that is a
            decision, not an oversight: it hides the *filter* affordance, and
            sorting is a different control. hideHeader removes this trigger
            structurally, because it lives inside the v-if="!hideHeader"
            header block above. No count badge here, and no screen-size check
            of its own (unlike the Filter button's badge above, which reaches
            for the deprecated `breakpoints` mixin helper for its own `sm`
            check) -- this trigger needs neither.
          -->
          <button
            v-if="showSortTrigger"
            id="datatable-sort-button"
            aria-label="Sort"
            class="flex relative gap-2 justify-center items-center p-4 md:px-5 md:py-2.5 text-gray-900 whitespace-nowrap bg-white rounded-full md:rounded-lg border border-solid"
            data-cy="datatable-sort-btn"
            @click="toggleSortSheet"
          >
            <CloseIcon v-if="sortSheetOpen" height="12" />
            <SortIcon v-else class="h-3" />
          </button>
          <slot name="leftActions"></slot>
          <slot name="rightActions"
            ><div
              class="dtRightActions hidden flex flex-col gap-4 md:flex-row md:items-center"
            ></div
          ></slot>
          <slot name="actions"></slot>
          <HintTooltip
            v-if="!noDownload"
            ref="hintTooltip"
            text="This is already queued"
          >
            <template #target>
              <Button
                data-cy="download-report-btn"
                class="md:rounded-lg rounded-full md:w-auto md:h-auto w-[46px] h-[46px]"
                @click="exportReport"
              >
                <div class="flex items-center gap-1">
                  <i class="fas fa-download" />
                  <span class="hidden md:block">Download</span>
                </div>
              </Button>
            </template>
          </HintTooltip>
        </div>
      </div>
    </header>
    <div :class="['col-filter', { active: tagsVisible }]">
      <section
        ref="appliedFilters"
        class="flex gap-4 justify-between items-center py-4 px-6 font-medium border-t"
      >
        <div
          class="flex flex-wrap max-h-[5rem] gap-1.5 items-center text-indigo-900 overflow-y-auto"
        >
          <span class="text-sm text-gray-900">Filtering by:</span>
          <div
            v-for="filter in state.appliedFilters"
            :key="filter.name"
            class="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-4 py-2 flex items-center gap-4 rounded-lg dark:bg-blue-900 dark:text-blue-300 !cursor-pointer"
            @click="removeFilter(filter)"
          >
            {{ filterChipText(filter) }}
            <CloseIcon height="10" />
          </div>
          <div
            v-for="(value, name) in state.searches"
            :key="name"
            class="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-4 py-2 flex items-center gap-4 rounded-lg dark:bg-blue-900 dark:text-blue-300 !cursor-pointer"
            @click="clearSearch(name)"
          >
            {{ searchTermLabel(name) }}: "{{ value }}"
            <CloseIcon height="10" />
          </div>
        </div>
        <button
          class="gap-1 self-stretch py-0.5 pr-1.5 pl-1.5 my-auto text-gray-600 rounded hidden md:block whitespace-nowrap"
          @click="clearAllFilters"
        >
          Clear All Filters
        </button>
      </section>
    </div>
    <table :id="id" ref="table" style="min-width: 100%">
      <thead>
        <tr>
          <th
            v-for="(col, idx) in tableColumnsConfig"
            :key="`col_${col.data}`"
            :ref="
              (el) => {
                headerRefs[`header_${col.data}`] = el;
              }
            "
            :class="[
              col.headerClassName || col.className,
              `${id}_header_${col.data}`,
              { 'filter-active': hasFilterApplied(col) },
              { 'actions-column-header': col.data === 'actions' },
              /* These two classes must remain exact complements of the single
                 headerHostsFilters expression -- never both, never neither. */
              { 'filters-open h-[100px]': headerHostsFilters },
              { 'h-[50px]': !headerHostsFilters },
              getSortClasses(col, idx)
            ]"
          >
            <p v-if="!col.checkbox">
              {{ columnLabel(col.title, col.data) }}
            </p>
            <div v-if="col.checkbox" class="datatable-header-checkbox">
              <DatatableCheckboxV2
                :disabled="headerCheckboxDisabled"
                :modelValue="headerCheckboxValue"
                class="datatable-checkbox"
                @update:model-value="handleSelectAll"
              />
            </div>
          </th>
        </tr>
      </thead>
      <tbody ref="tbody" class="table-data" data-cy="table-data" />
      <tfoot
        v-show="state.hasAggregates && !responsiveActive"
        class="datatable-footer-aggregates"
      >
        <tr>
          <th
            v-for="col in tableColumnsConfig"
            :key="col.data"
            class="aggregate-totals-header"
            :class="col.footerClassName"
          ></th>
        </tr>
        <tr>
          <td
            v-for="col in tableColumnsConfig"
            :key="col.data"
            class="aggregate-totals-value"
            :class="[col.footerClassName, col.footerCellClass]"
          ></td>
        </tr>
      </tfoot>
    </table>
    <Teleport
      v-if="bottomRow && responsiveActive && state.aggregateTotals.length"
      :to="bottomRow"
    >
      <section
        class="datatable-aggregates-panel"
        :class="{ expanded: totalsExpanded }"
        data-cy="aggregates-panel"
      >
        <button
          type="button"
          class="datatable-aggregates-toggle"
          :aria-expanded="totalsExpanded"
          :aria-controls="`${id}-aggregates`"
          data-cy="aggregates-toggle"
          @click="totalsExpanded = !totalsExpanded"
        >
          Totals
        </button>
        <dl
          v-show="totalsExpanded"
          :id="`${id}-aggregates`"
          class="datatable-aggregates-stack"
          data-cy="aggregates-stack"
        >
          <template v-for="total in state.aggregateTotals" :key="total.index">
            <dt>{{ total.title }}</dt>
            <dd>
              <component
                :is="total.component"
                v-if="total.component"
                v-bind="total.componentProps"
              />
              <!-- The footer already renders these totals as HTML; same trust. -->
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-else
                v-html="
                  total.col.aggregateRender
                    ? total.col.aggregateRender(total.value)
                    : total.value
                "
              />
              <!-- eslint-enable vue/no-v-html -->
            </dd>
          </template>
        </dl>
      </section>
    </Teleport>
    <SafeTeleport
      v-if="!hidePageLengthSelect && state.drawn && !state.drawing"
      :to="`#pageSelectPortal-${id}`"
    >
      <DatatablePageLength
        v-model="state.pageLength"
        :options="pageLengthOptions"
      />
    </SafeTeleport>
    <SafeTeleport
      v-if="showJumpToPage && state.drawn && !state.drawing"
      :to="`#pageJumpPortal-${id}`"
    >
      <PageJumpInput
        :total-pages="state.totalPages"
        :current-page="state.currentPage"
        @jump="goToDatatablePage"
      />
    </SafeTeleport>
    <div v-if="state.drawn">
      <div v-for="col in visibleColumns" :key="`filter_${col.data}`">
        <SafeTeleport :to="filterTeleportTarget(col.data)">
          <div v-if="col.checkbox" class="datatable-header-checkbox pt-[10px]">
            <DatatableCheckboxV2
              :disabled="headerCheckboxDisabled"
              :modelValue="headerCheckboxValue"
              class="datatable-checkbox"
              @update:model-value="handleSelectAll"
            />
          </div>
          <div
            v-else
            :ref="
              (el) => {
                filterRefs[`filter_${col.data}`] = el;
              }
            "
            :class="
              filterOccupiesSheet(col)
                ? 'datatable-sheet-filter'
                : [
                    'col-filter',
                    {
                      active: headerHostsFilters,
                      // The sheet owns filtering below md, so a header copy
                      // must not keep width Responsive has already fitted.
                      hidden: filterSurfaceIsSheet
                    }
                  ]
            "
          >
            <div
              :class="[col.filterClassName, 'pt-1.5']"
              @click.stop="handleFilterFocus"
            >
              <DatatableFilter
                v-if="columnFilter(col)"
                :ref="
                  (el) => {
                    filterComponentRefs[
                      `${columnFilter(col)?.name}FilterComponent`
                    ] = el;
                  }
                "
                :filter="columnFilter(col)"
                :data-cy="`${col.filterName || col.data}_column_filter`"
                no-label
                class="column-filter"
                :version="2"
                :datatable-id="id"
                @refresh-table="refreshData"
                @filter-applied="emit('filter-applied', $event)"
                @filter-removed="emit('filter-removed', $event)"
              />
              <div v-else-if="col.searchable !== false">
                <TextField
                  class="full-width"
                  input-class="column-search-field"
                  :data-cy="`${col.filterName || col.data}_column_search`"
                  :placeholder="getSearchPlaceholder(col)"
                  :model-value="getColumnSearchValue(col.data)"
                  search
                  @update:model-value="handleColSearch($event, col.data)"
                  @blur="endColumnSearch(col.data)"
                />
              </div>
            </div>
          </div>
        </SafeTeleport>
      </div>
    </div>
    <!-- Rendered AFTER the per-column SafeTeleport loop above on purpose: Vue
         patches siblings in order, so that loop has already moved every
         filter back to its header cell by the time this sheet unmounts. -->
    <DatatableFilterSheet
      v-if="responsiveActive && filterSheetOpen"
      ref="filterSheet"
      :columns="sheetFilterColumns"
      :table-id="id"
      :applied-count="appliedFilterCount"
      :filtered-count="state.filteredRecordCount"
      @slots-ready="onSheetSlotsReady"
      @hide-sheet="onFilterSheetHidden"
      @clear-all="clearAllFilters"
    />
    <DatatableSortSheet
      v-if="responsiveActive && sortSheetOpen"
      ref="sortSheet"
      :columns="sortSheetColumns"
      :table-id="id"
      :current-sort="currentOrderPair"
      :current-sort-label="currentSortLabel"
      @sort="applySort"
      @hide-sheet="onSortSheetHidden"
    />
  </div>
</template>

<script setup lang="ts">
// Docs: app/javascript/components/DatatableV3.md
import { debounce } from "lodash";
import API from "serviceshift-ui/api-client";
import SSUIEventBus from "serviceshift-ui/shared/src/lib/event_bus";
import {
  type Component,
  computed,
  createApp,
  defineComponent,
  h,
  markRaw,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toRaw,
  useSlots,
  watch
} from "vue";
import { useRouter } from "vue-router";
import { SafeTeleport } from "vue-safe-teleport";
import { reportRumError } from "@/lib/datadog";
import { EventBus } from "@/lib/event_bus";
import { applyFixedColumnsEnd } from "@/lib/fixedColumnsEnd";
import { isEmpty, isFalsy } from "@/lib/helpers";
import {
  countRenderedPageButtons,
  shouldShowPageJump
} from "@/lib/paginationJump";
import { trackMobileWebEvent } from "@/lib/pendo";
import { normalizePhoneNumber } from "@/lib/phone";
import { extractProps, renderVueComponent } from "@/lib/renderVueComponent.js";
import { findMatches } from "@/lib/tree";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Button from "@/components/Button.vue";
import { resolveDatatableComponent } from "@/components/datatableComponentRegistry";
import DatatableFilterSheet from "@/components/DatatableFilterSheet.vue";
import DatatablePageLength from "@/components/DatatablePageLength.vue";
import DatatableSortSheet from "@/components/DatatableSortSheet.vue";
import HintTooltip from "@/components/HintTooltip.vue";
import PageJumpInput from "@/components/PageJumpInput.vue";
import TextField from "@/components/TextField.vue";
import { useMobileWebOptimization } from "@/composables/useMobileWebOptimization";
import CONSTANTS from "@/Constants";
import SortIcon from "@/images/chevron-sort.svg?component";
import SearchIcon from "@/images/search.svg?component";
import CloseIcon from "@/images/x.svg?component";
import { capitalize, columnLabel } from "@/mixins";
import { breakpoints } from "@/mixins";
import DatatableCheckboxV2 from "@/Reports/DatatableCheckboxV2.vue";
import DatatableFilter from "@/Reports/DatatableFilter.vue";
import store from "@/vuex/store";
import { getDefaultsToSet } from "./datatableDefaults";
import {
  inspectDuplicateFilters,
  lastNavigationType
} from "./datatableDuplicateProbe";
import { pageStateOptions } from "./datatablePageState";
import {
  pageDisplayStart,
  pageParamName,
  pageParamValue,
  readPageFromUrl
} from "./datatablePageUrl";
import { resolveHeaderTarget } from "./datatableTeleport";
import {
  applyWidthFloors,
  measureLateSizedCells
} from "./responsiveLateWidths";
import {
  decideWithTapSlot,
  TAP_SLOT_WIDTH,
  tapSlotWidthFor
} from "./responsiveTapSlot";
import { useStorage } from "./useStorage";

const { CALL_MODAL_SEARCH_TERM_KEY } = CONSTANTS;

const router = useRouter();

const STACKED_FOOTER_HEIGHT = 140;
const UNSTACKED_FOOTER_HEIGHT = 100;

interface Config {
  paging?: boolean;
  retrieve?: boolean;
  processing?: boolean;
  serverSide?: boolean;
  pageLength?: number;
  pagingType?: string;
  lengthMenu?: ((number | string)[] | null)[] | null;
  columns: ColumnsEntity[] | null;
  bLengthChange?: boolean;
  language?: Language;
  fixedColumns?: {
    start: string;
    end: string;
  };
  order?: ((number | string)[] | null)[] | null;
  responsive?: boolean;
  layout?: {
    topStart: string;
    topEnd: string;
  };
  scrollX?: boolean;
  scrollY?: boolean;
  colReorder?: boolean;
  autoWidth?: boolean;
  vueComponents?: VueComponentsEntity[] | null;
}
interface AggregateComponentEntity {
  rootId: string;
  component: Component;
  propName: string;
  options?: { props?: Record<string, unknown> };
}
interface AggregateTotal {
  index: number;
  col: ColumnsEntity;
  title: string;
  value: unknown;
  component?: Component;
  componentProps?: Record<string, unknown>;
}
interface ColumnsEntity {
  data: string | null;
  className?: string;
  headerClassName?: string;
  title?: string;
  searchable?: boolean;
  orderable?: boolean;
  checkbox?: boolean;
  filterName?: string;
  filterClassName?: string;
  visible?: boolean;
  responsivePriority?: number;
  searchPlaceholder?: string;
  footerClassName?: string;
  footerCellClass?: string;
  headerEditIcon?: boolean;
  render?: any;
  aggregateTitle?: string;
  aggregateRender?: (value: unknown) => string;
  aggregateComponent?: AggregateComponentEntity;
  nullAggregate?: boolean;
}
interface Language {
  infoFiltered: string;
  paginate: Paginate;
  processing: string;
}
interface Paginate {
  first: string;
  previous: string;
  next: string;
  last: string;
}
interface VueComponentsEntity {
  rootId: string;
  component?: any;
  options: {
    store?: any;
    props?: any;
  };
}
export interface Filter {
  type: string;
  name: string;
  label: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  items?: any;
  multiple?: boolean;
  value?: string;
  default_value?: string;
  visible?: boolean;
  // Prefix the applied-filter chip with the filter's label, for tables where two
  // filters share option labels (e.g. two "Current" status filters).
  label_in_chip?: boolean;
}
interface RowEntity {
  checkbox_disabled?: string;
  internalId?: string;
  [key: string]: any;
}

const configDefaults: Config = {
  retrieve: true,
  processing: true,
  serverSide: true,
  pageLength: 25,
  paging: true,
  pagingType: "numbers",
  lengthMenu: [
    [10, 25, 50, 100],
    ["10", "25", "50", "100"]
  ],
  columns: [],
  bLengthChange: false,
  language: {
    infoFiltered: "",
    paginate: {
      first: "«",
      previous: "‹",
      next: "›",
      last: "»"
    },
    processing:
      '<i class="fa-duotone fa-solid fa-spinner fa-spin fa-3x fa-fw text-gray-500"></i><span class="sr-only">Loading...</span>'
  },
  fixedColumns: { start: "0", end: "0" },
  responsive: false,
  layout: {
    topStart: "div",
    topEnd: "div"
  },
  scrollX: false,
  scrollY: false,
  colReorder: false,
  autoWidth: false
};

const emit = defineEmits([
  "download-report",
  "row-select",
  "row-click",
  "after-init",
  "draw-complete",
  "page-change",
  "resize",
  "update:bulk-select-ids",
  "filter-applied",
  "filter-removed",
  "filters-cleared",
  "column-searched"
]);

const props = withDefaults(
  defineProps<{
    scrollableHeight?: string;
    scrollable?: boolean;
    bulkSelectAcrossPages?: boolean;
    // Keeps the items selected as you go from page to page. Similar to GMail.
    bulkSelectKeepAlive?: boolean;
    bulkSelect?: boolean;
    bulkSelectKey?: string;
    bulkSelectIds?: Array<string | number>;
    csvFileName?: string;
    useDatatableExportCsv?: boolean;
    config: Config;
    // AJAX endpoint for the rows. Omit it when passing `rows`.
    source?: string;
    // Client-side mode: the table draws these rows itself and never fetches.
    // Requires an `id`, which keys its stored state in place of `source`.
    rows?: Record<string, unknown>[];
    // Bad name, should be ajax data config. Used to manipulate the data request sent to the server
    mapRecords?: () => Record<string, any>;
    minHeight?: number;
    // Enables row click to select rows
    selectable?: boolean;
    // Highlights a row based on the record.id
    selectedId?: string | number;
    id?: string;
    // Removes CSS card and box-shadow styling
    flat?: boolean;
    // Callback fired after the datatable is reloaded
    onReload?: () => void;
    // When this live table reloads its rows in place, should it jump back to
    // page 1? Applies per reload and keeps nothing. Restoring the page across a
    // navigation is handled separately — via the URL for id'd tables (see
    // datatablePageUrl), or restorePageOnReturn for those the URL can't reach.
    resetPaging?: boolean;
    // Restore the page across a navigation that rebuilds the table from scratch,
    // for tables the URL scheme can't reach: server-rendered mounts with no id
    // (e.g. the inventory product lists). Persists only the paging offset to
    // storage, keyed by source, and composes with resetPaging. Tables with an id
    // use the URL instead, so leave this off for them.
    restorePageOnReturn?: boolean;
    filters?: Filter[];
    noDownload?: boolean;
    downloadUrl?: string;
    defaultPageLength?: number;
    hideGlobalFilters?: boolean;
    hidePageLengthSelect?: boolean;
    mapRowClass?: () => string;
    mapRowId?: () => string | number;
    // Persists column searches across page loads
    persistSearch?: boolean;
    title?: string;
    // Hides Filter button
    hideFilters?: boolean;
    //hides entire header, used in nested tables
    hideHeader?: boolean;
    autoHeight?: boolean;
    willEmitDownloadReport?: () => boolean;
    maxHeight?: number;
    subpage?: boolean;
  }>(),
  {
    scrollableHeight: "300px",
    bulkSelectKey: "id",
    bulkSelectIds: () => [],
    csvFileName: "orders",
    mapRecords: undefined,
    selectedId: "",
    id: "",
    onReload: () => {},
    restorePageOnReturn: false,
    filters: () => [],
    downloadUrl: "",
    defaultPageLength: 25,
    mapRowClass: () => "",
    mapRowId: () => "",
    title: "",
    autoHeight: true,
    willEmitDownloadReport: () => false,
    maxHeight: undefined,
    minHeight: 200,
    source: "",
    rows: undefined
  }
);

const storage = useStorage();
const clientMode = computed(() => Array.isArray(props.rows));
const stateKey = computed(() =>
  clientMode.value ? `rows:${props.id}` : props.source
);
const state = reactive({
  availableInternalIds: [],
  internalSelectedIds: [] as Array<string | number>,
  rowsInView: [] as RowEntity[],
  customRowVueApps: [] as Array<{ column: string; component: any }>,
  datatable: null as any,
  currentPage: 1,
  totalPages: 0,
  // recordsDisplay (all pages), not resultCount (this page). Null while unknown.
  filteredRecordCount: null as number | null,
  visiblePageButtons: 0,
  drawn: false,
  drawing: true,
  selectedNode: null,
  hasAggregates: true,
  aggregateTotals: [] as AggregateTotal[],
  pageLength: props.defaultPageLength.toString(),
  filtersVisible: true,
  searches: {},
  isDestroying: false,
  searchCols: [],
  rowSelectionRange: [null, null],
  usedShiftKeyLast: false,
  lastCheckedValue: false,
  appliedFilters: [] as Filter[],
  filtersOpen: false,
  shadows: {
    left: false,
    right: false,
    top: false,
    bottom: false
  },
  resultCount: 0,
  currentOrder: null as Config["order"]
});

const hintTooltip = ref<any>(null);
const table = ref<any>(null);
const tbody = ref<any>(null);
// The totals panel starts closed. It teleports into DataTables' sticky bottom
// layout row (datatable_tailwind.css wraps it onto its own line there) so it
// stacks above the info and pager instead of under them; the row exists from
// init on and is read once the API is in hand.
const totalsExpanded = ref(false);
const bottomRow = shallowRef<HTMLElement | null>(null);
const filterRefs = ref<any>({});
const filterComponentRefs = ref<any>({});
const headerRefs = ref<any>({});
const datatableRef = ref<any>(null);
// The sheet's per-column slot host, published by DatatableFilterSheet once its
// DOM settles. Read by filterTeleportTarget AND the per-column wrapper class
// (occupancy, below), so "where does this filter go" and "is this filter in
// the sheet right now" are one signal and cannot disagree. Kept out of
// `reactive(state)`: an element has no interior worth deep-tracking, so
// shallowRef is enough, and state's lifetime is persisted-and-watched UI
// state (its filtersOpen entry is mirrored into storage) that a DOM element
// does not belong in.
const sheetSlotHost = shallowRef<HTMLElement | null>(null);
// Deliberately NOT state.filtersOpen, which is the header's own
// persisted-per-source reveal state. A phone user opening or closing the
// sheet must not write it, or their close would silently collapse the
// header filter row for a desktop user of the same table's source. Also
// kept out of reactive(state) for the same lifetime reason as
// sheetSlotHost.
const filterSheetOpen = ref(false);
const filterSheet = ref<InstanceType<typeof DatatableFilterSheet> | null>(null);
// The sort sheet's own open state, owned the same way and for the same
// reason as filterSheetOpen above: a local ref, never state.filtersOpen,
// which is the header's persisted-per-source reveal flag and belongs to a
// different surface entirely.
const sortSheetOpen = ref(false);
const sortSheet = ref<InstanceType<typeof DatatableSortSheet> | null>(null);
// The set of config-array indexes Responsive currently reports as collapsed,
// fed by its own resize/collapse report (below) -- never from
// `visibleColumns`, which only knows the static `visible` config flag and
// knows nothing about Responsive's runtime hiding. Reassigned to a fresh Set
// on every update rather than mutated in place, so Vue's reactivity tracks
// the change without wrapping a Set in `reactive()`.
const collapsedColumnIndexes = ref<Set<number>>(new Set());

const slots = useSlots();

// COMPUTED
// The combined count of applied filters and column searches -- the single
// source both the Filter button's badge and the sheet's clear-all visibility
// read, so the two can never disagree on "is anything applied right now".
const appliedFilterCount = computed(
  () => state.appliedFilters.length + Object.keys(state.searches).length
);
const tagsVisible = computed(() => appliedFilterCount.value > 0);
// Responsive mode is a per-table opt-in (`config.responsive`) that only takes
// effect in the mobile layout: flag on and viewport below md. Above md, or with
// the flag off, the table is the same one it was before Responsive existed.
const { enabled: mobileLayout, pending: mobileLayoutPending } =
  useMobileWebOptimization();
const responsiveActive = computed(
  () => props.config.responsive === true && mobileLayout.value
);
// Surface: does a Filter tap on this table mean the sheet, not the header?
// True from first paint on an opted-in phone table, sheet open or closed,
// flag resolved or pending -- the `pending` term is the point: below md,
// before the tenant fetch settles, responsiveActive is false but the answer
// is "not yet", not "no", and the header with its Filter button is already
// on screen (the template does not await mobileLayoutDecided()). Guessing
// the fallback (header) surface in that window would write a cache shared
// with the desktop user of this table's source, so toggleFiltersOpen treats
// the pending window as a no-op rather than falling through.
//
// Reuses the exact gate useMobileWebOptimization already carries (its own
// header comment calls itself "a near-verbatim extraction of
// useSheetPlacement.ts's gate"), plus this table's own config.responsive
// opt-in. Calling useSheetPlacement() here too would duplicate that gate and
// risk it drifting out of sync with this one.
const filterSurfaceIsSheet = computed(
  () =>
    props.config.responsive === true &&
    (mobileLayout.value || mobileLayoutPending.value)
);
// Header reveal: is this table revealing its filters in the header row right
// now? The exact complement of filterSurfaceIsSheet against state.filtersOpen,
// so the <th> height pair below can never see a state with neither class --
// reachable if only one side of this pair is ever rewritten without the other.
const headerHostsFilters = computed(
  () => state.filtersOpen && !filterSurfaceIsSheet.value
);
// The Filter button's own open/closed icon. Keyed on filterSurfaceIsSheet
// rather than responsiveActive so the icon stays honest in the pending
// window, where a tap does nothing and the icon must therefore not change.
const filterButtonOpen = computed(() =>
  filterSurfaceIsSheet.value ? filterSheetOpen.value : state.filtersOpen
);

const withClass = (className: string | undefined, added: string) =>
  className?.split(/\s+/).includes(added)
    ? className
    : [className, added].filter(Boolean).join(" ");

const tableColumnsConfig = computed(() => {
  let columns = props.config.columns || [];
  // If we are using a bulk select display, the first column needs to be a checkbox
  if (props.bulkSelect) {
    const colDef = {
      checkbox: true,
      data: null,
      orderable: false,
      searchable: false,
      bSortable: false,
      bSearchable: false,
      title: "",
      sTitle: "",
      render: renderCheckboxCell
    };
    if (columns[0]?.checkbox) {
      columns[0] = colDef;
    } else {
      columns.unshift(colDef);
    }
  }
  columns = columns.map((col) => {
    if (col.data === "actions") {
      col.title = "Action";
      col.searchable = false;
      col.orderable = false;
    }
    if (slots[`column-${col.data}`]) {
      col.render = customColumnRender;
    }
    return col;
  });
  if (responsiveActive.value) {
    // Responsive pins by priority, not by FixedColumns: the actions and
    // bulk-select columns must stay visible at every width. New objects, so the
    // caller's config is untouched when Responsive turns off again.
    columns = columns.map((col) =>
      col.data === "actions" || col.checkbox
        ? { ...col, className: withClass(col.className, "all") }
        : col
    );
  }
  return columns;
});

/**
 * Allows the user to implement a custom template for a column. The template name
 * must match the `data` property in the column configuration.
 * If the user does not define a definition, the default logic is applied where
 * we render the component from the response.
 *
 * ex:
 * { data: "date_added", ... }
 * <template #column-date_added="rowData">...</template>
 *
 */
const customColumnRender = (data: any, type: string, row: any, meta: any) => {
  const column = tableColumnsConfig.value[meta.col].data;
  if (!column) return;

  // eslint-disable-next-line vue/one-component-per-file
  const vueComponent = defineComponent({
    setup(_) {
      return () => {
        const slotContent =
          slots[`column-${column}`]?.({
            ...row,
            columnValue: data
          }) || "";

        return h("div", { class: "datatable-custom-column" }, [slotContent]);
      };
    }
  });
  state.customRowVueApps.push({ column, component: markRaw(vueComponent) });
  return `<div class="datatable-custom-column-${column}-${
    state.customRowVueApps.length - 1
  }">${data}</div>`;
};
const renderCheckboxCell = (data: any, type: string, row: any, meta: any) => {
  const id = row[props.bulkSelectKey];
  const cell = state.datatable.cell({ row: meta.row, column: meta.col });

  // eslint-disable-next-line vue/one-component-per-file
  const vueComponent = defineComponent({
    setup() {
      return () =>
        h("div", [
          h(DatatableCheckboxV2, {
            "onUpdate:modelValue": () => handleSelect(row),
            onClick: (e) => {
              // Selecting a row must not also trigger the delegated
              // row-click handler (e.g. opening an invoice detail).
              e.stopPropagation();
              handleSelectRange(e, cell);
            },
            modelValue: state.internalSelectedIds.indexOf(id as never) > -1,
            disabled: row.checkbox_disabled === "true"
          })
        ]);
    }
  });

  state.customRowVueApps.push({
    column: "bulk-select-checkbox",
    component: markRaw(vueComponent)
  });
  return `<div class="datatable-custom-column-bulk-select-checkbox-${
    state.customRowVueApps.length - 1
  }"></div>`;
};
const visibleColumns = computed(() =>
  tableColumnsConfig.value.filter((col) => col.visible !== false)
);
// The columns the sheet renders a row for. Empty unless responsiveActive --
// deliberately NOT filterSurfaceIsSheet, because a sheet must never mount on a
// pending flag answer (responsiveActive is the gate for anything that
// actually renders the sheet). Excludes the bulk-select checkbox column (no
// filter concept) and any column with neither a filter nor a column search;
// those keep resolving to their header cell through resolveHeaderTarget's
// untouched fall-through tiers. Not gated on being non-empty itself:
// the Filter button already exists on every table and removing it here would
// take an existing affordance away at some widths, a behaviour change outside
// this ticket.
const sheetFilterColumns = computed(() => {
  if (!responsiveActive.value) return [];
  return visibleColumns.value.filter(
    (col) => !col.checkbox && (columnFilter(col) || col.searchable !== false)
  );
});
const sheetFilterColumnKeys = computed(
  () => new Set(sheetFilterColumns.value.map((col) => col.data))
);
// Location: is THIS column's filter physically in the sheet right now? Read
// from sheetSlotHost rather than filterSheetOpen: sheetSlotHost is the very
// ref filterTeleportTarget reads below, so the class and the filter's actual
// location are one signal and cannot disagree -- including across the tick
// between filterSheetOpen flipping true and the sheet's slots existing, during
// which filterSheetOpen already says "sheet" while every filter is still in
// its header cell.
const filterOccupiesSheet = (col: ColumnsEntity) =>
  sheetSlotHost.value !== null && sheetFilterColumnKeys.value.has(col.data);
// Resolve the single visible header cell a column's filter/search box teleports
// into, scoped to this datatable's own container. A shared global selector
// resolves to whichever matching header is attached to the document, so a stale
// Vue app left mounted-but-detached after a navigation teleports its filter into
// the live page's header and stacks a duplicate box. See TOF-612 and
// ./datatableTeleport. Below md on an opted-in table, sheetSlotHost hands
// resolveHeaderTarget its additive first tier: the sheet's own per-column
// slot, resolved before the header-cell tiers are even considered.
const filterTeleportTarget = (colData: string | null) =>
  resolveHeaderTarget(
    table.value?.closest(".dt-container") ?? null,
    `${props.id}_header_${colData}`,
    sheetSlotHost.value
  );
// The columns the sort sheet will render: every column Responsive currently
// reports collapsed AND sortable. Uses the same orderability test
// the header already uses (col.orderable !== false, getSortClasses below)
// rather than a second one, so the sort control and the header's own sort
// indicators can never disagree about what is sortable. No `visible` test
// needed: a column DataTables has hidden is reported as on-screen (true, not
// collapsed) by Responsive's own map, so it never enters
// collapsedColumnIndexes in the first place -- a redundant visible check
// here would be dead code.
const collapsedSortableColumns = computed(() =>
  tableColumnsConfig.value
    .map((col, index) => ({ col, index }))
    .filter(
      ({ col, index }) =>
        collapsedColumnIndexes.value.has(index) && col.orderable !== false
    )
    .map(({ col, index }) => ({
      index,
      label: columnLabel(col.title, col.data)
    }))
);
// Enriches the read-only collapsedSortableColumns with each column's data
// key, for the sort sheet's Pendo change payload (schema, not content --
// the data key, never a row value). Layered on top rather than added to
// collapsedSortableColumns itself, which stays exactly {index, label} --
// its own tests pin that shape.
const sortSheetColumns = computed(() =>
  collapsedSortableColumns.value.map(({ index, label }) => ({
    index,
    label,
    data: tableColumnsConfig.value[index]?.data ?? null
  }))
);
// Reads the [index, direction] pair the same convention getSortClasses uses
// per order-array entry (below) -- first entry only, index at position 0,
// direction at position 1. Factored here so the sheet's currentSort binding
// and currentSortLabel both read the exact same pair and can never disagree
// about what is sorted. Deliberately NOT shared code with getSortClasses
// itself, which is left untouched (no change to header sorting) and which
// folds every order-array entry for its own multi-column marking (accepted
// as out of scope for the sheet).
const currentOrderPair = computed<[number, string] | null>(() => {
  const order = state.currentOrder || props.config.order;
  const entry = order?.[0];
  if (!entry || entry[0] === null || entry[0] === undefined || !entry[1]) {
    return null;
  }
  return [entry[0] as number, entry[1] as string];
});
// The label for the sheet's "current sort" line -- resolved here because
// this is the only place that can name a column outside the sheet's own row
// list (the sorted column may not itself be collapsed).
const currentSortLabel = computed(() => {
  const pair = currentOrderPair.value;
  if (!pair) return null;
  const col = tableColumnsConfig.value[pair[0]];
  if (!col) return null;
  return columnLabel(col.title, col.data);
});
// Gated on responsiveActive AND a non-empty collapsed-sortable list, not on
// responsiveActive alone. responsiveActive is the md token (853px) plus the
// per-table opt-in, while Responsive decides collapse by fitting columns
// against its own bands (1024/768/480/320) -- the deviation DISCOVERY.md
// records. Between roughly 768px and 853px an opted-in table can fit every
// column and collapse nothing, and a "Sort" button that opens a sheet
// holding only the empty-list explanatory line is dead weight at exactly the
// width where header sorting is fully available. Nothing collapsed means
// nothing for this control to do.
const showSortTrigger = computed(
  () => responsiveActive.value && collapsedSortableColumns.value.length > 0
);
const checkableRowsInView = computed(() =>
  state.rowsInView.filter((row) => row.checkbox_disabled !== "true")
);
const hasSelectedAll = computed(() => {
  if (props.bulkSelectAcrossPages) {
    return state.availableInternalIds.every((id) => {
      return state.internalSelectedIds.includes(id);
    });
  } else {
    return checkableRowsInView.value.every((row) => {
      return (
        state.internalSelectedIds.indexOf(row[props.bulkSelectKey] as never) >
        -1
      );
    });
  }
});
const headerCheckboxDisabled = computed(() => {
  const rowList = props.bulkSelectAcrossPages
    ? state.rowsInView
    : checkableRowsInView.value;

  return rowList.length === 0;
});
const headerCheckboxValue = computed(() => {
  const rowList = props.bulkSelectAcrossPages
    ? state.rowsInView
    : checkableRowsInView.value;

  return rowList.length === 0 ? false : hasSelectedAll.value;
});
// DataTables keeps this as _iDisplayLength and works out the end of the page by
// adding it to the current offset. Handed over as a string that addition
// concatenates: page 2 of 25 reported "Showing 26- 2,525 of 101,607" instead of
// 26-50, on every page but the first. state.pageLength itself stays a string,
// because DatatablePageLength's options and v-model are strings.
const pageLengthNumber = computed(() => Number(state.pageLength));

const pageLengthOptions = computed(() => {
  const pageLengths = [10, 25, 50, 100];
  return pageLengths.map((length) => ({
    label: length + " items",
    value: length.toString()
  }));
});
const globalFilters = computed(() => {
  const filterNames = tableColumnsConfig.value.map(
    (col) => col.filterName || col.data
  );
  return props.filters?.filter(
    (filter) => !filterNames.includes(filter.name) && filter.visible !== false
  );
});
const setSearchFilters = () => {
  if (clientMode.value) return;
  const params = getQueryVariables();
  const filterNames = tableColumnsConfig.value.map(
    (col) => col.filterName || col.data
  );

  filterNames
    .filter((name) => params[name])
    .filter((name) => props.filters.every((filter) => filter.name !== name))
    .map((name) => (state.searches[name] = params[name]));
  if (params.term) {
    state.searches.term = params.term;
  }

  const hasColumnSearches = Object.keys(state.searches).some(
    (key) => key !== "term"
  );
  if (!hasColumnSearches) {
    state.searchCols = [];
    return;
  }

  state.searchCols = tableColumnsConfig.value.map((col) => {
    if (!col.data || col.searchable === false) {
      return { search: "" };
    }

    return { search: state.searches[col.data] || "" };
  });
};
const hasActionsColumn = computed(() => {
  return (
    tableColumnsConfig.value.some((col) => col.data === "actions") &&
    state.resultCount > 0
  );
});

// WATCHERS
watch(hasActionsColumn, (val, oldVal) => {
  if (val === oldVal) return;
  // Responsive mode pins the actions column by class; there is no FixedColumns
  // instance to toggle.
  if (responsiveActive.value) return;
  // Toggle the trailing frozen column when the actions column shows/hides.
  //
  // This first fires when resultCount goes 0 -> non-zero, which races the
  // FixedColumns extension constructing against the table. Applying before it
  // has attached throws inside the library and aborts the reactive flush, which
  // is how teleported controls end up stranded. See lib/fixedColumnsEnd for why
  // guarding the call itself does nothing.
  if (applyFixedColumnsEnd(state.datatable, val)) return;

  // Not attached yet. The init config seeded fixedColumns.end from
  // hasActionsColumn while resultCount was still 0, so dropping this toggle
  // would leave the actions column unfrozen for the whole page. Re-apply on the
  // next draw, by which point the extension has constructed.
  $(table.value).one("draw.dt", () => {
    applyFixedColumnsEnd(state.datatable, val);
  });
});
// Redraw in place, keeping the page.
watch(
  () => props.rows,
  () => {
    if (clientMode.value && state.datatable) drawRows(false);
  }
);
watch(tagsVisible, () => {
  setTimeout(() => {
    setScrollBodyHeight();
  }, 200);
});
watch(
  () => state.rowsInView,
  () => {
    updateSelectedRowsDisplay();
  }
);
watch(
  () => props.bulkSelectIds,
  (val, oldVal) => {
    if (val === oldVal || val === undefined) return;
    if (JSON.stringify(val || []) === JSON.stringify(oldVal || [])) return;
    state.internalSelectedIds = [...val];
  },
  { deep: true }
);
watch(
  () => props.mapRowClass,
  () => updateSelectedRowsDisplay()
);
watch(
  () => state.internalSelectedIds,
  (val) => {
    if (
      JSON.stringify(val || []) === JSON.stringify(props.bulkSelectIds || [])
    ) {
      return;
    }
    updateSelectedRowsDisplay();
    emit("update:bulk-select-ids", val);
  },
  { deep: true }
);
watch(
  () => props.selectedId,
  (val, oldVal) => {
    if (val !== oldVal) {
      selectInitialRow();
    }
  }
);
watch(
  () => state.drawn,
  (val, oldVal) => {
    if (val !== oldVal && val) {
      selectInitialRow();
    }
  }
);
watch(
  () => state.pageLength,
  (val, oldVal) => {
    if (val !== oldVal) {
      state.datatable.page.len(pageLengthNumber.value).draw();
    }
  }
);
watch(
  () => state.filtersOpen,
  (val, oldVal) => {
    if (val !== oldVal) {
      // Cache filter state per table source
      storage.merge("tableFilterStates", { [stateKey.value]: val });
      // A table whose closed filters take no width resizes its columns when they open or close.
      nextTick(() => state.datatable?.columns.adjust());
      // if (!val) {
      //   nextTick(() => {
      //     $(".col-filter.active").remove();
      //   });
      // }
    }
  }
);
watch(
  () => state.appliedFilters,
  (val, oldVal) => {
    if (val !== oldVal) {
      setTimeout(() => {
        const filtersRowShown = val.length > 0 && oldVal.length === 0;
        const filtersRowHidden = val.length === 0 && oldVal.length > 0;
        if (filtersRowShown || filtersRowHidden) {
          SSUIEventBus.$emit("reposition-dropdown-options");
        }
      }, 300);
    }
  }
);

// LIFECYCLE HOOKS
onBeforeMount(() => {
  // Fire-and-forget: these overrides are expensive enough to be worth keeping off
  // pages with no datatable, and nothing here may wait on them — rendering and
  // mount() must not depend on a network fetch. preloadErrorHandler keeps a
  // failed fetch from rejecting.
  import("./datatable_tailwind.css");

  // Set default filter state from cache
  const filterCache = storage.get("tableFilterStates");
  state.filtersOpen = filterCache ? filterCache[stateKey.value] : false;
});
onMounted(mount);
onBeforeUnmount(() => {
  state.isDestroying = true;
  destroyDatatable();
  EventBus.$off("refresh-datatable", refreshData);
  EventBus.$off("clear-datatable-bulk-select", clearAllSelections);
  window.removeEventListener("resize", handleResize);
});

// Make sure to reset the refs before each update.
onBeforeUpdate(() => {
  filterRefs.value = {};
  filterComponentRefs.value = {};
  headerRefs.value = {};
});

// METHODS
// Below md the flag decides which table to build, so wait for it instead of
// painting the desktop table and rebuilding; at desktop width `pending` is
// already false and nothing waits.
const mobileLayoutDecided = () =>
  new Promise<void>((resolve) => {
    if (!mobileLayoutPending.value) return resolve();
    watch(mobileLayoutPending, () => resolve(), { once: true });
  });

// The child row Responsive has just built under `row`, or null. Its renderer
// inserts it as the parent's next sibling with class "child".
const childRowOf = (row: { node: () => Element | null } | null) => {
  const next = row?.node()?.nextElementSibling;
  return next?.classList.contains("child") ? next : null;
};

const initChildRowTooltips = (childRow: Element) => {
  const targets = Array.from(
    childRow.querySelectorAll("[data-tippy-content]")
  ).filter((node) => !(node as { _tippy?: unknown })._tippy);
  if (targets.length) tippy(targets);
};

const attachResponsiveHandler = () => {
  // Responsive builds a child row from the column templates, so the Vue roots
  // in it are unmounted copies; mount them the way a draw does. Tooltips are
  // copies too, and initTippy only runs on draw, so give the new row its own.
  $(table.value).on(
    "responsive-display.dt",
    (_event, _datatable, row, shown: boolean, update: boolean) => {
      if (!shown) return;
      const childRow = childRowOf(row);
      renderVueComponents(childRow ?? document);
      if (childRow) initChildRowTooltips(childRow);
      // Responsive redisplays open child rows with `update` after columns
      // re-fit; only a user's expand counts.
      if (update || !responsiveActive.value) return;
      trackMobileWebEvent("datatable_row_expand", {
        surface: "datatable",
        // Most report tables carry no id; the page path still names the table.
        table_id: props.id || window.location.pathname,
        hidden_column_count: $(table.value).find("thead th.dtr-hidden").length
      });
    }
  );
};

// Responsive gives every row a tabIndex once the row is the expand target, but
// its own Enter handler listens on cells only, so a focused row would be inert.
// Responsive reopens expanded rows after each reload by selecting "#<rowId>".
// When an opened row is gone, DataTables falls back to matching that string
// as CSS, and a numeric id ("#1") is not a valid selector, so the draw throws.
// Bound before DataTables is built so this preXhr runs before Responsive's:
// the open rows are closed before Responsive records them, and the ones still
// in the new data are reopened through Responsive's own display routine.
const attachChildRowReopen = () => {
  let reopen: string[] = [];
  $(table.value).on("preXhr.dt", () => {
    if (!state.datatable) return;
    reopen = [];
    state.datatable.rows().every(function () {
      if (!this.child.isShown()) return;
      reopen.push(String(this.id()));
      this.child.hide();
    });
  });
  $(table.value).on("draw.dt", () => {
    if (!reopen.length || !state.datatable) return;
    const settings = state.datatable.settings()[0];
    reopen.forEach((id) => {
      const rowObj = settings.ids?.[id];
      if (rowObj) {
        settings._responsive?._detailsDisplay(
          state.datatable.row(rowObj.idx),
          false
        );
      }
    });
    reopen = [];
  });
};

const attachRowKeyboardHandler = () => {
  $(tbody.value).on("keyup", "tr", function (event) {
    if (event.key !== "Enter" || event.target !== this) return;
    if (!tableCollapsed()) return;
    (this as HTMLElement).click();
  });
};

// Links and controls inside a collapsed row keep their own behaviour and do not
// also toggle the row. Responsive's toggle is a jQuery handler delegated from
// this same <tbody> to "tr", and jQuery runs delegated handlers from the
// clicked element outward, so this one runs first and ends jQuery's walk before
// the row. Native propagation must go on: Turbo and the document-level
// [data-modal-url] handler act on link clicks at the document.
const ROW_CONTROLS =
  "a[href], button, input, select, textarea, label, [role='button']";
const attachRowControlHandler = () => {
  $(tbody.value).on("click", ROW_CONTROLS, (event) => {
    if (!tableCollapsed()) return;
    event.isPropagationStopped = () => true;
  });
};

// Responsive's payload marks SHOWN columns true
// (dataTables.responsive.js:970-971,1001-1003) -- the API's own name
// ("responsiveHidden" / the resize event's "shown" map) is the opposite of
// its polarity, so `false` is the collapsed one. A non-array payload (the
// no-jQuery trigger() branch stashes the extra args on event._args instead
// of delivering them to a jQuery handler) carries no information about
// collapse and must not be read as "nothing is collapsed".
const collapsedIndexesFromShownMap = (shown: unknown): Set<number> | null => {
  if (!Array.isArray(shown)) return null;
  const collapsed = new Set<number>();
  shown.forEach((isShown, index) => {
    if (isShown === false) collapsed.add(index);
  });
  return collapsed;
};

// responsive-resize.dt is the only event that fires for a Responsive-driven
// collapse: responsive-display.dt (bound above) is child-row expand/collapse
// only, and column-visibility.dt never fires for Responsive, which
// deliberately bypasses DataTables' own visibility API in favour of a raw
// CSS display toggle. A separate handler, not an extension of
// attachResponsiveHandler's callback above -- the two events answer
// different questions, and sharing a callback would silently bind the
// collapse set to child-row expansion.
const attachResponsiveCollapseHandler = () => {
  $(table.value).on(
    "responsive-resize.dt",
    (_event, _datatable, shown: unknown) => {
      const collapsed = collapsedIndexesFromShownMap(shown);
      if (collapsed === null) return;
      collapsedColumnIndexes.value = collapsed;
    }
  );
};

// Responsive runs its first sizing pass inside its own constructor, which may
// have finished before attachResponsiveCollapseHandler's listener existed --
// so read once, explicitly, on every draw (drawCallback, see
// attachRenderHandler). Guarded in three parts, each commented separately:
// they fail differently, and a single "is it there?" check only catches the
// first.
const readInitialCollapsedColumns = () => {
  // 1. Nothing to read without an active, live table.
  if (!responsiveActive.value || !state.datatable) return;
  const settings = state.datatable.settings()[0];
  // 2. The guard that matters: the plugin must be attached to THIS table.
  // columns().responsiveHidden() answers false for every column when
  // settings._responsive is missing, and false means *collapsed* under this
  // API's inverted polarity -- an attachment-blind read on a table the
  // plugin never touched would report the whole table collapsed and hand
  // the sort sheet every column.
  if (!settings?._responsive) return;
  const columnsApi = state.datatable.columns();
  // 3. responsiveHidden is actually a registered function, because the
  // plugin ships from the CDN bundle and is absent from the Vitest harness
  // (spec/javascript/mocks/$.js:2-4), where an unguarded call throws.
  if (typeof columnsApi.responsiveHidden !== "function") return;
  // The API returns an Api instance, not a plain array -- Array.isArray and
  // Array.prototype methods do not apply to it without .toArray() first.
  const collapsed = collapsedIndexesFromShownMap(
    columnsApi.responsiveHidden().toArray()
  );
  if (collapsed !== null) collapsedColumnIndexes.value = collapsed;
};

// The two sides of `md` are two DataTables configurations (Responsive on one,
// FixedColumns and scrollX on the other) and Responsive cannot be toggled on
// a live table, so this instance never switches modes. DatatableReportV3 keys
// the table on the layout side: a tablet crossing md unmounts one table and
// mounts a fresh one, sheets, handlers and all.

async function mount() {
  checkInitialProps();
  await setInitialFilters();
  setSearchFilters();
  await mobileLayoutDecided();
  if (state.isDestroying) return;
  initTable();
  attachScrollObserver();
  attachRowClickHandler();
  attachRowKeyboardHandler();
  attachRowControlHandler();
  attachResponsiveHandler();
  attachResponsiveCollapseHandler();
  attachPageHandler();
  attachCSVExport();
  initTippy();
  attachPagingStateSync();
  if (!clientMode.value) EventBus.$on("refresh-datatable", refreshData);
  EventBus.$on("clear-datatable-bulk-select", clearAllSelections);
  window.addEventListener("resize", handleResize);
  // @ts-expect-error wront type for ImportMeta
  if (import.meta.hot) {
    // @ts-expect-error wront type for ImportMeta
    import.meta.hot.on("vite:afterUpdate", attachHotModuleHandlers);
  }
}
const columnFilter = (col: ColumnsEntity) =>
  props.filters.find(
    (filter) =>
      filter.name === (col.filterName || col.data) && filter.visible !== false
  );
const drawRows = (resetPaging: boolean) =>
  state.datatable?.clear().rows.add(props.rows).draw(resetPaging);
const reload = (callback: () => void, resetPaging: boolean) => {
  if (!clientMode.value) {
    state.datatable?.ajax.reload(callback, resetPaging);
    return;
  }
  drawRows(resetPaging);
  callback();
};
const refreshData = (options?: { resetPaging?: boolean }) =>
  reload(props.onReload, options?.resetPaging ?? props.resetPaging);
const refreshDataWithCallback = (callback = () => {}) =>
  reload(callback, props.resetPaging);
const onSheetSlotsReady = (host: HTMLElement | null) => {
  sheetSlotHost.value = host;
};
// The single teardown every dismissal path funnels into, via hide-sheet: the
// sheet closes from four paths of its own -- close icon, backdrop, Escape and
// swipe -- and none of them calls the parent, so any parent-ordered close
// sequence would cover the one path users can least easily take (its own
// backdrop covers the Filter button while it is open) and miss the four they
// will. Does NOT touch state.filtersOpen -- that flag is the header's own
// persisted-per-source reveal state.
//
// This teardown closes no dropdowns and emits nothing on hide-all-dropdowns:
// a payload-less emit throws inside Select's click-out handler and leaves the
// dropdown open anyway, and the repair belongs in serviceshift-ui, which this
// component does not touch. Left uncovered: Escape, a swipe, and the
// responsiveActive watcher -- the backdrop, close icon and Filter button are
// themselves document clicks that Select's own click-out listener closes
// unaided.
const onFilterSheetHidden = () => {
  sheetSlotHost.value = null;
  filterSheetOpen.value = false;
};
const closeFilterSheet = () => {
  // The one genuine hole: if the sheet ref is already gone, there is no
  // hide-sheet coming, so run the teardown directly. A leave transition that
  // never resolves is not a hole worth guarding -- Vue's whenTransitionEnds
  // resolves immediately when it finds no transition and otherwise arms a
  // setTimeout backstop, so @after-leave always fires eventually.
  if (!filterSheet.value) {
    onFilterSheetHidden();
    return;
  }
  // The filters stay where they are until hide-sheet arrives; this is
  // deliberate; it is the only sequencing every dismissal path shares.
  filterSheet.value.close();
};
const toggleFiltersOpen = () => {
  if (!filterSurfaceIsSheet.value) {
    state.filtersOpen = !state.filtersOpen;
    return;
  }
  if (!responsiveActive.value) {
    // The pending window: the flag answer has not arrived, so the tap has no
    // unambiguous meaning; mount() is itself still awaiting
    // the same signal, so the table body is not drawn and there is nothing to
    // filter yet; and the fallback branch would write a cache shared with the
    // desktop user of this table's source. Deliberate no-op, not a queued "do
    // it when the answer lands" -- a tap the user made before the surface
    // existed is not a tap at that surface.
    return;
  }
  if (filterSheetOpen.value) {
    closeFilterSheet();
    return;
  }
  // Guard the OPEN direction only (mutual exclusion with the sort sheet): a
  // blanket early return at the top of this handler would also
  // refuse to CLOSE an already-open filter sheet while sortSheetOpen happens
  // to be set, and a user who cannot dismiss the sheet in front of them is a
  // worse outcome than the stacking this guards against. The close path
  // above runs unconditionally; only the open is refused.
  if (sortSheetOpen.value) return;
  filterSheetOpen.value = true;
};
// The sort sheet's own single teardown, mirroring onFilterSheetHidden for
// the same reason: the sheet closes from four dismissal paths of its own
// and none of them calls the parent, so this is the one signal every path
// funnels into via hide-sheet.
const onSortSheetHidden = () => {
  sortSheetOpen.value = false;
};
const closeSortSheet = () => {
  // The one genuine hole, same as closeFilterSheet: if the sheet ref is
  // already gone, there is no hide-sheet coming, so run the teardown
  // directly.
  if (!sortSheet.value) {
    onSortSheetHidden();
    return;
  }
  sortSheet.value.close();
};
const toggleSortSheet = () => {
  if (sortSheetOpen.value) {
    closeSortSheet();
    return;
  }
  // Guard the OPEN direction only, mirroring the filter button's own guard
  // above -- neither guard can refuse a close, so neither can strand a sheet
  // the user is trying to dismiss.
  if (filterSheetOpen.value) return;
  sortSheetOpen.value = true;
};
// order() mutates the setting without drawing; draw() is what actually
// resyncs state.currentOrder through the existing drawCallback
// (attachRenderHandler), the same route a header click uses -- no extra
// sync code belongs here.
const applySort = ({
  index,
  direction
}: {
  index: number;
  direction: string;
}) => {
  state.datatable?.order(index, direction).draw();
  closeSortSheet();
};
const exportReport = debounce(() => {
  if (props.willEmitDownloadReport()) {
    emit("download-report");
  } else if (props.useDatatableExportCsv) {
    state.datatable.buttons(".buttons-csv").trigger();
  } else {
    const params = getQueryVariables();
    const normalizedParams = { ...params };

    props.filters.forEach((filter) => {
      const paramName = getFilterParamName(filter);
      if (!isFalsy(params[paramName]) && isFalsy(params[filter.name])) {
        normalizedParams[filter.name] = params[paramName];
      }
    });

    API.makeRequest(props.downloadUrl || window.location.pathname, {
      method: "POST",
      data: normalizedParams
    }).then((res) => {
      if (res.status === 204) hintTooltip.value?.show();
    });
  }
}, 500);
const removeFilter = async (filter) => {
  await updateQueryParam(getFilterParamName(filter), null, true);
  const filterInput =
    filterComponentRefs.value[`${filter.name}FilterComponent`];
  filterInput?.clearValue();
  if (!filterInput) {
    EventBus.$emit("clear-filter", filter);
  }
  refreshData();
};
const handleSelectAll = () => {
  if (props.bulkSelectAcrossPages) {
    bulkSelectAllAcrossPages();
    return;
  }

  const cachedHasSelectedAll = hasSelectedAll.value;

  checkableRowsInView.value.forEach((row) => {
    const id: never = row[props.bulkSelectKey] as never;
    const indexFound = state.internalSelectedIds.indexOf(id);
    if (cachedHasSelectedAll) {
      state.internalSelectedIds.splice(indexFound, 1);
      emit("row-select", row, false);
    } else {
      if (indexFound === -1) {
        state.internalSelectedIds.push(id);
        emit("row-select", row, true);
      }
    }
  });

  updateSelectedRowsDisplay();
};
const updateSelectedRowsDisplay = () => {
  if (!state.rowsInView.length) return;
  if (!state.datatable) return;
  const table = state.datatable;
  // Only drawn rows have nodes (deferRender), so mark the current page.
  table.rows({ page: "current" }).every(function (index) {
    const row = table.row(index);
    const id = row.data()[props.bulkSelectKey] as never;
    if (id) row.node().id = id;
    if (state.internalSelectedIds.indexOf(id) > -1) {
      row.node().classList.add("dt-selected-row");
    } else {
      row.node().classList.remove("dt-selected-row");
    }
    if (props.mapRowClass) {
      const rowClass = props.mapRowClass({
        data: row.data(),
        rowIndex: index,
        id,
        row
      });
      if (rowClass) {
        const newRowClasses = [];
        if (typeof rowClass === "string") newRowClasses.push(rowClass as never);
        // @ts-expect-error FIXME
        if (Array.isArray(rowClass)) newRowClasses.push(...rowClass);
        row.node().classList.add(...newRowClasses);
      }
    }
  });
};
const bulkSelectAllAcrossPages = () => {
  if (!hasSelectedAll.value) {
    markAllSelectionsAcrossPages();
  } else {
    clearAllSelections();
  }
};
const markAllSelectionsAcrossPages = () => {
  state.internalSelectedIds = [...state.availableInternalIds];

  state.rowsInView.forEach((row) => {
    emit("row-select", row, true);
  });

  updateSelectedRowsDisplay();
};
const clearAllSelections = () => {
  state.internalSelectedIds.splice(0, state.internalSelectedIds.length);

  state.rowsInView.forEach((row) => {
    emit("row-select", row, false);
  });

  updateSelectedRowsDisplay();
};
const attachScrollObserver = () => {
  const root = $(table.value).closest(".dt-scroll")[0];
  root?.addEventListener("scroll", setScrollShadows);
};
const setScrollShadows = () => {
  nextTick(() => {
    const root = $(table.value).closest(".dt-scroll")[0];
    if (!root) return;
    const scrolledToBottom =
      root.scrollTop + root.clientHeight >= root.scrollHeight;
    state.shadows = {
      left: root.scrollLeft > 0,
      right:
        Math.abs(root.scrollWidth - root.clientWidth - root.scrollLeft) >= 1,
      bottom:
        !scrolledToBottom &&
        (root.scrollTop > 0 || root.scrollHeight > root.clientHeight),
      top: root.scrollTop > 0
    };
  });
};
const fixMisalignedColumns = () => {
  const tableHeaderCont = document.querySelector(
    ".dt-scroll-headInner"
  ) as HTMLElement;
  const headerContWidth = tableHeaderCont?.scrollWidth || 0;
  const headerTable = document.querySelector(
    ".dt-scroll-headInner table"
  ) as HTMLElement;
  const headerWidth = headerTable?.scrollWidth || 0;
  const scrollBodyWidth =
    document.querySelector(".dt-scroll-body")?.scrollWidth;
  if (!tableHeaderCont || !headerTable || !scrollBodyWidth) return;
  if (
    scrollBodyWidth &&
    (headerWidth < scrollBodyWidth || headerContWidth !== scrollBodyWidth)
  ) {
    // Manually adjust header width to account for scrollbars
    headerTable.style.width = `${scrollBodyWidth}px`;
    tableHeaderCont.style.width = `${scrollBodyWidth}px`;
  }
};
const hasFilterApplied = (col: ColumnsEntity) => {
  const hasSearch = state.searches[col.data];
  if (hasSearch) return true;
  const filter = columnFilter(col);
  if (!filter) return false;
  return state.appliedFilters.some((af) => af.name === filter.name);
};
const searchTermLabel = (colName: string) => {
  if (colName === "term") return "Term";
  const column = visibleColumns.value.find((col) => col.data === colName);
  if (!column) return "--";
  return columnLabel(column.title, column.data);
};
const filterChipText = (filter: Filter) =>
  filter.label_in_chip
    ? `${filter.label}: ${filterLabel(filter)}`
    : filterLabel(filter);

const filterLabel = (filter: Filter) => {
  switch (filter.type) {
    case "select": {
      const findOption = (value) =>
        filter.options?.find((opt) => opt.value == value) || {
          label: "--",
          value: "--"
        };
      if (filter.multiple && filter.value) {
        // parse selected values
        return filter.value
          ?.split(",")
          .map((val) => findOption(val).label)
          .join(", ");
      }
      return findOption(filter.value).label;
    }
    case "lineItemSelect": {
      // The filter value is an encoded "<kind-prefix>_<id>" list (e.g.
      // "t_265,ms_7"), opaque to users. Walk the items tree the filter
      // was built from, mirror LineItemSelect's kind → prefix derivation,
      // and map each encoded value back to its display name.
      if (!filter.value || !filter.items) return filter.value;
      const labelByValue = {};
      findMatches(filter.items, "").forEach((item) => {
        const prefix = item.kind
          .replace(/_/, " ")
          .split(" ")
          .map((word) => word[0])
          .join("");
        labelByValue[`${prefix}_${item.id}`] = item.name;
      });
      return String(filter.value)
        .split(",")
        .map((val) => labelByValue[val] || val)
        .join(", ");
    }
    case "toggle": {
      return filter.options && filter.options[Number(filter.value == "true")];
    }
    default:
      return filter.value;
  }
};
const clearAllSearches = () => {
  // Must remove all column level search values
  $(".column-search-field").val("");
  visibleColumns.value.forEach((col, i) => {
    updateQueryParam(col.data, null, true);
    state.datatable.column(i).search("");
  });
  if (!clientMode.value) {
    const updatedUrl = state.datatable.ajax.url().split("?")[0];
    state.datatable.ajax.url(updatedUrl);
  }
  state.searches = {};
};
const clearAllFilters = async () => {
  const clearedCount = state.appliedFilters.length;
  if (clearedCount > 0) {
    emit("filters-cleared", { filters_cleared_count: clearedCount });
  }
  state.appliedFilters = [];
  const params = getQueryVariables();
  const currentRouteQuery =
    router?.currentRoute && router.currentRoute.value
      ? router.currentRoute.value.query
      : {};
  const updatedQuery = { ...params, ...currentRouteQuery };
  let didUpdateQuery = false;
  props.filters.forEach((filter) => {
    const paramName = getFilterParamName(filter);
    if (paramName in updatedQuery) {
      delete updatedQuery[paramName];
      didUpdateQuery = true;
    }
  });
  // Guarded the same way setInitialFilters() guards its own router.replace
  // call: a router-less mount (server-rendered, no <router-view> host) must
  // not throw here just because a filter happened to be cleared.
  if (didUpdateQuery && router) {
    await router.replace({ query: updatedQuery });
  }
  // Must clear all filter query params
  EventBus.$emit("clear-datatable-filters", props.id);
  clearAllSearches();
  state.datatable.columns().draw();
};
const clearSearch = async (columnName: string) => {
  const value = state.searches[columnName];
  await updateQueryParam(columnName, null, true);
  delete state.searches[columnName];
  localStorage.removeItem(`search_${columnName}`);
  if (columnName === "term") {
    state.datatable.search("").draw();
    return;
  }
  if (!clientMode.value && state.datatable.ajax.url().includes(columnName)) {
    const updatedUrl = state.datatable.ajax
      .url()
      .replace(`${columnName}=${value}`, "");
    state.datatable.ajax.url(updatedUrl);
  }
  performColSearch("", columnName);
};
const performColSearch = debounce((value: string, columnName: string) => {
  const index = tableColumnsConfig.value.findIndex(
    (_col) => _col.data === columnName
  );
  const isPhoneColumn = columnName.toLowerCase().indexOf("phone") > -1;
  // Disable regex search for phone columns, so that additional "+" signs can't give back results
  state.datatable
    .column(index)
    .search(
      value,
      isPhoneColumn ? false : undefined,
      isPhoneColumn ? false : undefined
    )
    .draw();
}, 250);
const getSearchPlaceholder = (col: ColumnsEntity) => {
  if (col.data === "address") {
    return "Search Street";
  }
  return col.searchPlaceholder || "Search";
};
const getColumnSearchValue = (columnName: string) =>
  state.searches[columnName] || "";
// Columns whose search box has already reported a search since it last had
// focus. The debounce above only coalesces keystrokes typed within 250ms, so a
// pause mid-word would otherwise report the same search again, and the draw
// that follows every page or sort change would too. Not reactive: nothing
// renders from it.
const columnsSearchedThisFocus = new Set<string>();
const endColumnSearch = (columnName: string) => {
  columnsSearchedThisFocus.delete(columnName);
};
// One event per focus session per column. The term itself is deliberately not
// emitted — callers track that a search happened, and a search term is user
// data that has no place in analytics.
const reportColumnSearched = (columnName: string) => {
  if (columnsSearchedThisFocus.has(columnName)) return;
  columnsSearchedThisFocus.add(columnName);
  emit("column-searched", { column: columnName });
};
const handleColSearch = async (value: string, columnName: string) => {
  if (!value) {
    return clearSearch(columnName);
  }
  reportColumnSearched(columnName);
  const isPhoneColumn = columnName.toLowerCase().indexOf("phone") > -1;
  const normalizedValue = isPhoneColumn ? normalizePhoneNumber(value) : value;

  state.searches[columnName] = value;
  if (props.persistSearch) {
    localStorage.setItem(`search_${columnName}`, normalizedValue);
  }
  await updateQueryParam(columnName, normalizedValue, true);
  performColSearch(normalizedValue, columnName);
};
// The CDN build publishes DataTables as a global. Buttons 4 hangs off that
// object rather than jQuery's namespace, so the wrapper reaches for it here
// instead of `$.fn.DataTable`, whose copy of the core predates the extensions.
const dataTableGlobal = () => window["DataTable"];

const initTable = () => {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get(CALL_MODAL_SEARCH_TERM_KEY);
  const urlPage = readPageFromUrl(props.id);
  const DataTable = dataTableGlobal();
  if (DataTable) {
    DataTable.defaults.column.orderSequence = ["asc", "desc"];
  }
  if (responsiveActive.value) attachChildRowReopen();
  // Client rows draw synchronously inside DataTable(), before it returns, so
  // the draw hooks need the instance from preInit (raw, as explained below).
  $(table.value).one("preInit.dt", (_event, settings) => {
    state.datatable = markRaw(new (dataTableGlobal().Api)(settings));
  });
  const api = $(table.value).DataTable({
    ...configDefaults,
    ...props.config,
    // Restore the page across a navigation for tables the URL can't reach
    // (server-rendered mounts with no id). Adds nothing unless the caller opted
    // in via restorePageOnReturn, so id'd tables keep using the URL below.
    ...pageStateOptions({
      enabled: props.restorePageOnReturn,
      key: stateKey.value,
      storage
    }),
    // Open on the page recorded in the URL so navigating away and back (or
    // opening a shared link) lands on the same page. Omitted when there is no
    // stored page, leaving the table on page 1.
    ...(urlPage
      ? { displayStart: pageDisplayStart(urlPage, pageLengthNumber.value) }
      : {}),
    // Deep-merge language so per-table overrides (e.g. emptyTable) layer on
    // top of the pagination/processing defaults rather than replacing them.
    language: { ...configDefaults.language, ...(props.config?.language || {}) },
    fixedColumns: { start: "0", end: hasActionsColumn.value ? "1" : "0" },
    // Responsive, FixedColumns and scrollX are mutually exclusive in DataTables,
    // so the mode decides them together. `config.responsive` is the opt-in
    // above, never the DataTables option itself; only the mobile layout turns
    // it into one. The whole row is the expand target: Responsive's inline
    // control would land in the first visible cell, which on a bulk-select
    // table is the checkbox cell.
    ...(responsiveActive.value
      ? {
          responsive: { details: { type: "column", target: "tr" } },
          fixedColumns: false,
          // No scroll box at all: DataTables builds one for any scroll value
          // other than the empty string, `false` included, and Responsive
          // must fit the columns against the page, not against a scroller
          // whose own bars change its width. The page scrolls instead.
          scrollX: undefined,
          scrollY: undefined
        }
      : { responsive: false }),
    searchCols: state.searchCols,
    pageLength: pageLengthNumber.value,
    columns: tableColumnsConfig.value,
    // 3.x stringifies the global search while building the server request, so
    // "no term" has to be the empty string rather than null.
    search: { search: searchTerm || "" },
    ...(clientMode.value
      ? { serverSide: false, data: [...(props.rows ?? [])] }
      : { ajax: { url: props.source, data: props.mapRecords } }),
    footerCallback: function () {
      if (state.isDestroying) return;
      setupFooter();
    },
    initComplete: function () {
      if (state.isDestroying) return;
      updateQueryParam(CALL_MODAL_SEARCH_TERM_KEY, null, true);
      // attachScrollableHandler();
      emit("after-init");
    },
    preDrawCallback: function () {
      if (state.isDestroying) return;
      // Forces a remount of custom vue components
      state.drawing = true;
      nextTick(() => (state.drawing = false));
      onBeforeDraw();
    },
    drawCallback: attachRenderHandler,
    infoCallback: function (oSettings, iStart, iEnd, iMax, iTotal) {
      // Fixes pricebook tasks price links in table after each draw
      document.dispatchEvent(new CustomEvent("datatable:draw"));
      cleanupPageLength();
      let count = "";
      if (iEnd == 0) {
        count = `<span class="font-semibold text-gray-900 dark:text-white">0</span> results`;
      } else {
        const format = (num) => parseInt(num, 10).toLocaleString();
        count = `Showing <span class="font-semibold text-gray-900 dark:text-white">${format(
          iStart
        )}</span><span class="font-semibold text-gray-900 dark:text-white">-</span>
          <span class="font-semibold text-gray-900 dark:text-white">${format(
            iEnd
          )}</span> of <span class="font-semibold text-gray-900 dark:text-white">${format(
            iTotal
          )}</span>`;
      }
      // Always include the pageSelectPortal for mounting DatatablePageLength
      // component. pageJumpPortal is deliberately separate: cleanupPageLength()
      // keeps only the last child of each portal, so sharing one would drop
      // whichever of the two controls rendered first.
      return (
        count +
        `<div id="pageSelectPortal-${props.id}"></div>` +
        `<div id="pageJumpPortal-${props.id}"></div>`
      );
    }
  });
  // Kept raw on purpose: through a reactive proxy, destroy() cannot find its
  // settings in DataTables' registry (an identity check), so the next init hits
  // "Cannot reinitialise DataTable" and hands back the dead instance.
  state.datatable = markRaw(api);
  if (responsiveActive.value) attachWidthObserver();
  bottomRow.value = api
    .table()
    .container()
    .querySelector(":scope > .row:last-child");
};
// A column's footer() node is its title cell in the first tfoot row; the value
// cell is the same DOM position in the next row. Responsive hides a column with
// display:none and leaves both cells in place, so a visible-ordinal lookup
// would drift while this stays aligned.
const footerValueCell = (titleCell: HTMLTableCellElement) =>
  (titleCell.parentElement?.nextElementSibling as HTMLTableRowElement | null)
    ?.cells[titleCell.cellIndex];

const setupFooter = () => {
  // A client table has no server aggregates, and its empty state is the
  // consumer's language.emptyTable, which the no-results override would replace.
  if (clientMode.value) {
    state.hasAggregates = false;
    state.aggregateTotals = [];
    return;
  }
  state.hasAggregates = true;
  const aggregates = (state.datatable.ajax.json() || {}).aggregates || {};
  if (props.bulkSelectAcrossPages) {
    state.availableInternalIds = (
      state.datatable.ajax.json() || {}
    ).full_internal_ids;
  }
  if (!state.datatable.data().toArray().length || isEmpty(aggregates)) {
    state.hasAggregates = false;
    state.aggregateTotals = [];
    setupNoResultsMessage();
    return;
  }
  const totals = collectAggregateTotals(aggregates);
  state.aggregateTotals = responsiveActive.value ? totals : [];
  if (!responsiveActive.value) writeFooterCells(totals);
};
const aggregateTitleFor = (col: ColumnsEntity) =>
  col.aggregateTitle || `Total ${capitalize(col.data)}`;
const hasTotal = (col: ColumnsEntity, value: unknown) =>
  Boolean(col.nullAggregate) || !isFalsy(value);
const aggregateTotalFor = (
  index: number,
  col: ColumnsEntity,
  value: unknown
): AggregateTotal => {
  const total: AggregateTotal = {
    index,
    col,
    title: aggregateTitleFor(col),
    value
  };
  if (col.aggregateComponent) {
    const { component, propName, options } = col.aggregateComponent;
    // The column config arrives through props, so the component is already a
    // reactive proxy; unwrap before marking it raw.
    total.component = markRaw(toRaw(component));
    // Declared props win over the total, as they do in extractProps.
    total.componentProps = { [propName]: value, ...options?.props };
  }
  return total;
};
// Every aggregate column the footer would show, in column order, read from the
// instance so the stack and the footer cannot disagree on visibility.
// Responsive-hidden columns still count; config-hidden ones do not.
const collectAggregateTotals = (
  aggregates: Record<string, unknown>
): AggregateTotal[] => {
  const dt = state.datatable;
  return dt
    .columns()
    .indexes()
    .toArray()
    .flatMap((index: number) => {
      const column = dt.column(index);
      const col: ColumnsEntity = column.init();
      const value = aggregates[col.data as string];
      return column.visible() && hasTotal(col, value)
        ? [aggregateTotalFor(index, col, value)]
        : [];
    });
};
const writeFooterCells = (totals: AggregateTotal[]) => {
  totals.forEach(({ index, col, title, value }) => {
    const titleCell = state.datatable.column(index).footer();
    const valueCell = footerValueCell(titleCell);
    $(titleCell).html(title);
    if (col.aggregateComponent) {
      const { component, rootId, propName, options } = col.aggregateComponent;
      const props = JSON.stringify({ [propName]: value });
      $(valueCell).html(`<div id="${rootId}" data=${props}></div>`);
      document.querySelectorAll(`#${rootId}`).forEach((node) => {
        renderVueComponent(node, component, options);
      });
    } else {
      $(valueCell).html(
        col.aggregateRender ? col.aggregateRender(value) : value
      );
    }
  });
};
const setupNoResultsMessage = () => {
  setTimeout(() => {
    if (!table.value) return;
    const dtEmpty = table.value.querySelector(".dt-empty");
    if (!dtEmpty) return;
    if (state.isDestroying) return;

    const noResultsMessageEl = document.createElement("div");
    noResultsMessageEl.classList.add("no-table-results-message");
    noResultsMessageEl.innerHTML = "No matching records found";
    dtEmpty.innerHTML = "";
    dtEmpty.appendChild(noResultsMessageEl);
    fixNoResultsMessageDisplay();
  });
};
const fixNoResultsMessageDisplay = () => {
  const datatable = $(datatableRef.value);
  const scrollableWrapperEl = datatable.find(".dt-scroll-body")[0];
  const innerTable = scrollableWrapperEl?.childNodes[0] as HTMLElement;
  if (!innerTable) return;
  // Only the empty state needs widening: with rows, the body table sizes the
  // header, so a pixel min-width here would feed back into DataTables. Restore
  // the template's `min-width: 100%` instead of clearing it — that is what lets
  // the table stretch back to the container after a narrower layout.
  if (!scrollableWrapperEl.querySelector(".dt-empty")) {
    innerTable.style.minWidth = "100%";
    return;
  }
  // Measure the header *table*, not the header container: the container is
  // padded by the scrollbar width so a comparison against it always wins by
  // 15px, and each resize would grow the table by one scrollbar.
  const headTable = datatable.find(".dt-scroll-headInner table")[0];
  const scrollHeadWidth = headTable
    ? headTable.getBoundingClientRect().width
    : datatable.find(".dt-scroll-head")[0].clientWidth;
  /* Fixes issue where no data message isn't full length */
  if (innerTable.scrollWidth < scrollHeadWidth) {
    innerTable.style.minWidth = `${scrollHeadWidth}px`;
  }
  scrollableWrapperEl
    .querySelectorAll(".no-table-results-message")
    .forEach((el) => {
      (el as HTMLElement).style.width = `${
        (scrollableWrapperEl.parentNode as HTMLElement).getBoundingClientRect()
          .width -
        42 + // offset for padding
        scrollableWrapperEl.scrollLeft
      }px`;
    });
};
const onBeforeDraw = () => {
  setLoadingPosition();
  state.rowSelectionRange = [null, null];
  // Clear custom Vue apps array to prevent stale components
  state.customRowVueApps = [];
};
const setLoadingPosition = () => {
  try {
    const datatableIndexMatch = table.value?.id || "dt";
    const leftOffset =
      $(`#${datatableIndexMatch}`).closest(".dt-scroll").scrollLeft() || 0;
    const processingSpinner = $(`#${datatableIndexMatch}_processing`);
    if (processingSpinner.length === 0) return;

    processingSpinner.css("left", `calc(50% + ${leftOffset}px)`);
  } catch (e) {
    // no-op
  }
};
// Fire the TOF-612 duplicate-search-box probe at most once per mounted table so
// a leaking view doesn't spam Error Tracking on every draw.
let duplicateProbeReported = false;
const probeForDuplicateFilters = () => {
  if (duplicateProbeReported) return;
  const report = inspectDuplicateFilters(
    table.value?.closest(".dt-container") ?? null,
    props.id,
    visibleColumns.value.map((col) => col.data)
  );
  if (!report) return;
  duplicateProbeReported = true;
  reportRumError("Datatable: duplicate search box detected", {
    ...report,
    view: window.location.pathname,
    navigationType: lastNavigationType()
  });
};
const syncPagingState = () => {
  const info = state.datatable?.page?.info();
  if (!info) return;
  // DataTables counts pages from 0; everything user-facing counts from 1.
  state.currentPage = info.page + 1;
  state.totalPages = info.pages;
  state.filteredRecordCount = Number.isFinite(info.recordsDisplay)
    ? info.recordsDisplay
    : null;

  const recount = () => {
    state.visiblePageButtons = countRenderedPageButtons(
      state.datatable?.table()?.container()
    );
  };
  recount();
  // DataTables redraws its paging control from an aoDrawCallback entry of its
  // own, and those run in registration order, so the buttons may still be the
  // previous draw's when we get here - on the very first draw, none at all.
  // Counting again once the current task has finished writing the DOM makes the
  // gate independent of that ordering. Verified in the browser: without this a
  // 2-page report showed the control on load and only corrected itself after
  // the next redraw.
  nextTick(recount);
};

const showJumpToPage = computed(() =>
  shouldShowPageJump(state.totalPages, state.visiblePageButtons)
);

const goToDatatablePage = (page: number) => {
  // "page" redraws without re-sorting or re-filtering, which for a serverSide
  // table is one request for the new offset.
  state.datatable?.page(page - 1).draw("page");
};

const attachRenderHandler = () => {
  if (!props.bulkSelectKeepAlive) {
    state.internalSelectedIds = [];
  }
  state.rowsInView = state.datatable.rows().data().toArray();
  const totalRows = clientMode.value
    ? state.datatable.rows().count()
    : state.datatable.ajax.json().recordsTotal;
  emit("draw-complete", {
    totalRows
  });
  state.drawn = true;
  // Sync DataTables' sort order into reactive state so Vue re-renders header sort classes
  state.currentOrder = state.datatable.order();
  readInitialCollapsedColumns();
  fitAroundTapSlot();
  void fitLateSizedCells();
  syncPagingState();
  renderVueComponents();
  setupFooter();
  state.searches = { ...state.searches };
  state.resultCount = state.datatable.rows().data().length;
  setAppliedFilters();
  setScrollBodyHeight();
  attachScrollObserver();
  attachColumnHoverHandler();
  nextTick(() => {
    setTimeout(() => {
      // Force fake scroll event to reposition filter menus
      document.dispatchEvent(new CustomEvent("scroll"));
      // Trigger resize to apply actions column shadow
      window.dispatchEvent(new CustomEvent("resize"));
      // Fix column alignment after drawing shadows
      fixMisalignedColumns();
      // Fix datatables column alignment issue
      state.datatable.columns.adjust();
      // Sample the settled header for the TOF-612 duplicate-search-box defect.
      probeForDuplicateFilters();
    });
  });
};
const renderVueComponents = (root: ParentNode = document) => {
  if (props.config.vueComponents) {
    props.config.vueComponents.forEach((componentDef) => {
      const { rootId, component, options = {} } = componentDef;
      const resolvedComponent = resolveDatatableComponent(component, rootId);
      if (!resolvedComponent) return;
      const nodes = root.querySelectorAll(`#${rootId}`);
      nodes.forEach((node) => {
        const data = extractProps(node, options);
        // eslint-disable-next-line vue/one-component-per-file
        const vueComponent = defineComponent({
          setup() {
            return () => h(resolvedComponent, { ...data });
          }
        });

        renderVueComponent(node, vueComponent, { store: options?.store });
      });
    });
  }
  if (state.customRowVueApps.length) {
    state.customRowVueApps.forEach(({ column, component }, rowIndex) => {
      const rootId = `datatable-custom-column-${column}-${rowIndex}`;
      const nodes = root.querySelectorAll(`.${rootId}`);
      nodes.forEach((node) => {
        // For checkbox components, mount directly without prop extraction
        if (column === "bulk-select-checkbox") {
          // Check if already mounted
          if (node.getAttribute("data-v-app") !== null) return;

          const app = createApp(component);
          if (store) {
            app.use(store);
          }
          app.mount(node);
        } else {
          renderVueComponent(node, component, { store });
        }
      });
    });
  }
};
const handleSelect = (row) => {
  const val = row[props.bulkSelectKey];
  const indexFound = state.internalSelectedIds.indexOf(val as never);
  if (indexFound > -1) {
    state.internalSelectedIds.splice(indexFound, 1);
    emit("row-select", row, false);
  } else {
    state.internalSelectedIds.push(val as never);
    emit("row-select", row, true);
  }
  updateSelectedRowsDisplay();
};
const handleSelectRange = (e, row) => {
  // Remove any selected text
  document.getSelection()?.removeAllRanges();
  const internalId = row.data()[props.bulkSelectKey];
  const isChecked = state.internalSelectedIds.indexOf(internalId as never) > -1;
  if (!e.shiftKey) {
    state.rowSelectionRange[0] = row.index().row;
    state.rowSelectionRange[1] = null;
    state.lastCheckedValue = !isChecked;
    state.usedShiftKeyLast = false;
    return;
  }
  e.preventDefault();
  if (state.usedShiftKeyLast) {
    state.rowSelectionRange[0] = row.index().row;
    selectRange(state.rowSelectionRange, state.lastCheckedValue);
    state.rowSelectionRange[1] = row.index().row;
  } else {
    state.rowSelectionRange[1] = row.index().row;
    selectRange(state.rowSelectionRange, state.lastCheckedValue);
  }
  state.usedShiftKeyLast = true;
};
/**
 * Check / Uncheck rows from range [startIndex, endIndex]
 * Ignores disabled rows
 * @param {array} customRange
 */
const selectRange = (customRange, checkedValue) => {
  if (!Number.isInteger(customRange[0]) || !Number.isInteger(customRange[1])) {
    return;
  }
  const rows = state.datatable.rows();
  const range = customRange.slice(0).sort((a, b) => a - b);

  // Go through the range and build the row data
  const rowToChecked = [];
  for (let i = range[0]; i <= range[1]; i++) {
    const rowData = rows.data()[i];
    if (!rowData) continue;
    const internalId = rowData[props.bulkSelectKey];
    if (rowData.checkbox_disabled === "true") continue;
    rowToChecked.push({
      internalId,
      checked: state.internalSelectedIds.indexOf(internalId as never) > -1,
      rowData,
      node: rows.nodes()[i]
    } as never);
  }

  // If every row is checked in the selection, we should do the opposite
  // and overide our original checked value
  let newCheckedValue = checkedValue;
  if (rowToChecked.every((row: RowEntity) => row.checked))
    newCheckedValue = false;
  if (rowToChecked.every((row: RowEntity) => !row.checked))
    newCheckedValue = true;
  state.lastCheckedValue = newCheckedValue;

  // Go through each row and check / uncheck
  rowToChecked.forEach((item: RowEntity) => {
    const internalSelectedIdIndex = state.internalSelectedIds.indexOf(
      item.internalId as never
    );
    if (newCheckedValue) {
      if (internalSelectedIdIndex === -1) {
        state.internalSelectedIds.push(item.internalId as never);
        emit("row-select", item.rowData, true);
      }
      item.node.classList.add("dt-selected-row");
    } else {
      if (internalSelectedIdIndex > -1) {
        state.internalSelectedIds.splice(internalSelectedIdIndex, 1);
        emit("row-select", item.rowData, false);
      }
      item.node.classList.remove("dt-selected-row");
    }
  });
};
const setAppliedFilters = () => {
  const params = getQueryVariables();
  state.appliedFilters = props.filters
    .filter((filter) => params[getFilterParamName(filter)])
    .map((filter) => ({
      ...filter,
      value: params[getFilterParamName(filter)]
    }));
};
const getFilterParamName = (filter: Filter) => {
  return props.id ? `${props.id}_${filter.name}` : filter.name;
};
const setScrollBodyHeight = () => {
  nextTick(() => {
    const body = $(table.value).closest(".dt-scroll")[0];
    if (!body) return;
    if (props.minHeight) {
      body.style.minHeight = `${props.minHeight}px`;
    }
    if (!props.autoHeight) return;

    const bodyTopOffset = body.getBoundingClientRect().top;

    const SCROLL_OFFSET = breakpoints().md
      ? STACKED_FOOTER_HEIGHT
      : UNSTACKED_FOOTER_HEIGHT;

    const calculatedHeight = window.innerHeight - bodyTopOffset - SCROLL_OFFSET;

    const height = props.maxHeight
      ? Math.min(calculatedHeight, props.maxHeight)
      : calculatedHeight;

    body.style.height = `${height}px`;
  });
};
const checkInitialProps = () => {
  if (props.bulkSelect) {
    if (props.bulkSelectIds?.length && !props.bulkSelectKeepAlive) {
      console.error(
        "Missing `bulk-select-keep-alive` property. Id's passed into the datatable will reset after first render."
      );
    }
  }
};

const setInitialFilters = async () => {
  const params = getQueryVariables();
  const defaultsToSet = getDefaultsToSet(
    props.filters.map((filter) => ({
      ...filter,
      name: getFilterParamName(filter)
    })),
    params
  );
  const scheduledTimeFilter = props.filters.find(
    (filter) => filter.name === "scheduled_time"
  );
  const scheduledTimeParam = scheduledTimeFilter
    ? getFilterParamName(scheduledTimeFilter)
    : "scheduled_time";

  if (!router) return;

  const currentRouteQuery =
    router?.currentRoute && router.currentRoute.value
      ? router.currentRoute.value.query
      : {};

  const newQuery = {
    ...params,
    ...currentRouteQuery,
    ...defaultsToSet
  };

  props.filters.forEach((filter) => {
    const prefixedName = getFilterParamName(filter);
    if (!(prefixedName in newQuery) && filter.name in newQuery) {
      newQuery[prefixedName] = newQuery[filter.name];
      delete newQuery[filter.name];
    }
  });

  if (newQuery[scheduledTimeParam] === "null") {
    delete newQuery[scheduledTimeParam];
  }

  if (newQuery === router?.currentRoute.value?.query) return;
  await router.replace({ path: window.location.pathname, query: newQuery });
  await nextTick();
};

// Responsive marks the table `collapsed` while any column is hidden. A tap on
// a collapsed row is its expand gesture, so it must not double as a row click.
const tableCollapsed = () =>
  responsiveActive.value &&
  table.value?.classList.contains("collapsed") === true;

const attachRowClickHandler = () => {
  $(tbody.value).on("click", "tr", function () {
    if (tableCollapsed()) return;
    const row = state.datatable.row(this).data();
    if (row) {
      if (props.selectable) {
        setSelectedRow(this);
      }
      // Emit row click with row data
      emit("row-click", row);
    }
  });
};
const setSelectedRow = (node) => {
  if (node) {
    // Highlight selected row
    state.datatable.$("tr.dt-selected-row").removeClass("dt-selected-row");
    $(node).toggleClass("dt-selected-row");
    state.selectedNode = node;
  }
};
const attachPageHandler = () => {
  // emits event after table is rendered for the first time
  $(table.value).on("page.dt", () => {
    emit("page-change");
    if (props.selectedId && !state.selectedNode) {
      selectInitialRow();
    }
  });
};
const selectInitialRow = () => {
  if (props.selectedId) {
    const node = findNodeById(props.selectedId);
    setSelectedRow(node);
  }
};
const findNodeById = (id) => {
  return state.datatable.row(`#${id}`).node();
};
// Write the current page into the URL (namespaced by table id) so it survives
// navigation and is shareable. updateQueryParam is idempotent and replaces
// history, so calling it on every draw neither spams history nor loops.
const syncPageToUrl = () => {
  const name = pageParamName(props.id);
  if (!name) return;
  const info = state.datatable?.page?.info();
  if (!info) return;
  updateQueryParam(name, pageParamValue(info.page + 1), true);
};

// A shared or stale URL can name a page beyond the current (filtered) result
// set. Land on the last real page instead of an empty one. Returns true when it
// re-paged, so the caller can defer the URL write to the resulting redraw.
const clampPageIfOutOfRange = () => {
  const info = state.datatable?.page?.info();
  if (!info || info.pages === 0) return false;
  if (info.page > info.pages - 1) {
    state.datatable.page(info.pages - 1).draw("page");
    return true;
  }
  return false;
};

const attachPagingStateSync = () => {
  // The last count is stale from every server request until its draw lands,
  // and stays unknown if the request fails.
  $(table.value).on("preXhr.dt", () => {
    state.filteredRecordCount = null;
  });
  // Belt and braces with the drawCallback above: recounting is idempotent, and
  // between the two there is no draw that can leave the count stale.
  $(table.value).on("draw.dt", () => {
    syncPagingState();
    if (clampPageIfOutOfRange()) return;
    syncPageToUrl();
  });
};
const initTippy = () => {
  // Initializes tippy tooltips when table is rendered
  $(table.value).on("draw.dt", () => {
    tippy("[data-tippy-content]");
    tippy("th:not(.dt-orderable-none) .dt-column-order", {
      content: "Sort"
    });
  });
};
// The scroll header and the body table agree on width once DataTables has
// measured a settled layout. Scoped to this table's container: a global lookup
// would read another table's header when two are on the page.
const headerAlignedWithBody = () => {
  const head = $(table.value)
    .closest(".dt-container")
    .find(".dt-scroll-headInner table")[0];
  if (!head || !table.value) return true;
  return (
    Math.abs(
      head.getBoundingClientRect().width -
        table.value.getBoundingClientRect().width
    ) < 1
  );
};

// A resize can flip the scrollbars mid-layout: DataTables measures the body
// while the narrow layout's horizontal bar is still there, pads the header for
// a vertical bar that then disappears, and one pass leaves the header a
// scrollbar width short of the body. Adjust on the next frames until the two
// agree, bounded so a table that can never align does not loop.
let settleFrame = 0;
const settleColumns = (passes = 3) => {
  cancelAnimationFrame(settleFrame);
  settleFrame = requestAnimationFrame(() => {
    if (!state.datatable || state.isDestroying) return;
    state.datatable.columns.adjust();
    fixNoResultsMessageDisplay();
    if (passes > 1 && !headerAlignedWithBody()) settleColumns(passes - 1);
  });
};

// Responsive measures on a copy of the table that cannot know where the tap
// slot will land (see responsiveTapSlot.ts), so its visibility decision is
// wrapped once per instance, on the first draw, and the table re-fitted with
// it. Private API: `_columnsVisibility` and `s.columns` are Responsive 4
// internals, checked for before use so a changed build falls back to the
// unwrapped fit.
interface ResponsiveInternals {
  s?: { columns?: Array<{ auto: boolean; minWidth: number }> };
  _columnsVisibility?: (breakpoint: string) => Array<boolean | string>;
  tapSlotFitted?: boolean;
}
const fitAroundTapSlot = () => {
  const dt = state.datatable;
  if (!responsiveActive.value || !dt) return;
  const instance: ResponsiveInternals | undefined =
    dt.settings()[0]?._responsive;
  const columns = instance?.s?.columns;
  const original = instance?._columnsVisibility;
  if (!instance || !columns || !original || instance.tapSlotFitted) return;
  instance.tapSlotFitted = true;
  const decide = original.bind(instance);
  instance._columnsVisibility = (breakpoint: string) => {
    const shown = (index: number) => dt.column(index).visible() !== false;
    const leftmost = columns.findIndex(
      (column, index) => column.auto && shown(index)
    );
    const tableNode = dt.table().node() as HTMLElement;
    const measuredOn =
      tableNode.classList.contains("collapsed") && leftmost !== -1
        ? leftmost
        : null;
    // Responsive re-measures on every fit, so the floors go back on each time.
    applyWidthFloors(
      columns,
      new Map(
        [...lateWidthFloors].map(([index, width]) => [
          index,
          index === measuredOn ? width + TAP_SLOT_WIDTH : width
        ])
      )
    );
    const available = (dt.table().container() as HTMLElement).offsetWidth;
    const slotWidth = tapSlotWidthFor(!!tableNode.closest(".datatable-report"));
    return decideWithTapSlot(
      columns,
      shown,
      measuredOn,
      available,
      () => decide(breakpoint),
      slotWidth
    );
  };
  dt.responsive?.recalc();
};

// Responsive's measuring copy sees late-drawing cells (responsiveLateWidths.ts)
// at their undrawn width. After each draw their real widths are measured and
// fed to the wrapped decision above as floors, and the table re-fitted if they
// moved. A later draw supersedes an earlier one still measuring.
let lateWidthFloors = new Map<number, number>();
let lateFitRun = 0;
const fitLateSizedCells = async () => {
  const dt = state.datatable;
  if (!responsiveActive.value || !dt) return;
  const instance: ResponsiveInternals | undefined =
    dt.settings()[0]?._responsive;
  if (!instance?.tapSlotFitted) return;
  const run = ++lateFitRun;
  const tableNode = dt.table().node() as HTMLTableElement;
  const floors = new Map<number, number>();
  for (const index of dt.columns().indexes().toArray() as number[]) {
    const cells = dt
      .cells(null, index, { page: "current" })
      .nodes()
      .toArray() as HTMLElement[];
    const widths = await measureLateSizedCells(cells, tableNode);
    if (widths.length) floors.set(index, Math.max(...widths));
  }
  if (run !== lateFitRun || state.isDestroying || dt !== state.datatable) {
    return;
  }
  const moved =
    floors.size !== lateWidthFloors.size ||
    [...floors].some(([index, width]) => lateWidthFloors.get(index) !== width);
  lateWidthFloors = floors;
  if (moved) dt.responsive?.recalc();
};

const handleResize = () => {
  emit("resize");
  // Responsive fits the columns itself and there is no scroll box to size or
  // align. A wrapper adjust here would answer Responsive's column-sizing with
  // another adjust, and the two can chase each other until the stack blows.
  // The width observer below asks Responsive for its re-fit.
  if (responsiveActive.value) return;
  setScrollBodyHeight();
  setScrollShadows();
  settleColumns();
};
// Responsive re-fits on its own only for orientationchange, column-sizing
// (never fired here: no autoWidth, no scroll box) and its own AJAX draws. It
// also records each column's minimum width from the DOM on its first fit, so
// a table built while its container is display:none (a tab not yet opened)
// measures 0 for every column. Both gaps are the table's width changing: on a
// window resize, or when its container appears. Width only: a re-fit changes
// heights, never the width the container gives, so this cannot chase itself.
let widthObserver: ResizeObserver | null = null;
const attachWidthObserver = () => {
  if (typeof ResizeObserver === "undefined") return;
  let lastWidth = 0;
  widthObserver = new ResizeObserver(([entry]) => {
    const width = entry.contentRect.width;
    if (width > 0 && width !== lastWidth) {
      state.datatable?.responsive?.recalc();
    }
    lastWidth = width;
  });
  widthObserver.observe(state.datatable.table().container());
};
const detachWidthObserver = () => {
  widthObserver?.disconnect();
  widthObserver = null;
};
const attachHotModuleHandlers = () => {
  state.customRowVueApps.forEach((app: any) => {
    if (app && app.$destroy) app.$destroy();
  });
};
const attachColumnHoverHandler = () => {
  nextTick(() => {
    const table = state.datatable;
    $(datatableRef.value).on(
      "mouseenter ",
      ".dt-scroll-head table th",
      function () {
        // $(".datatable-v3 .dt-scroll-head table").on("mouseenter ", "th", function () {
        // Remove highlight from all columns
        table
          .columns()
          .nodes()
          .flatten() // Reduce to a 1D array
          .to$() // Convert to a jQuery object
          .removeClass("cell-hover");

        // Add highlight to mouseover column
        table
          .column(this)
          .nodes()
          .to$() // Convert to a jQuery object
          .addClass("cell-hover");

        // Remove highlight from all headers
        table.columns().header().flatten().to$().removeClass("cell-hover");

        // Add highlight to mouseover headers
        table.columns(this).header().to$().addClass("cell-hover");
      }
    );
    $(".datatable-v3 .dt-scroll-head table").on(
      "mouseleave ",
      "th",
      function () {
        $(".datatable-v3 table td").removeClass("cell-hover");
        $(".datatable-v3 table th").removeClass("cell-hover");
      }
    );
  });
};
const destroyDatatable = () => {
  if (!state.datatable) return;

  detachWidthObserver();
  $(table.value).off();
  $(tbody.value).off();
  state.datatable.destroy(true);
};
const handleFilterFocus = (e) => {
  // Triggers dropdowns to check if they should open or close
  // This is needed because we are stopping propagation of events
  // on filter clicks otherwise we would sort everytime we click a filter
  SSUIEventBus.$emit("hide-all-dropdowns", e);
};
const getSortClasses = (col: ColumnsEntity, columnIdx: number): string => {
  const classes: string[] = [];
  // Add default datatable ordering classes
  if (col.orderable !== false) {
    classes.push("dt-orderable-asc");
    classes.push("dt-orderable-desc");
  } else {
    classes.push("dt-orderable-none");
  }
  // Get current sort order and set active class
  const getActiveSortClass = (order: Config["order"], colIdx) => {
    if (!order || !order[0]) return "";
    return order.reduce((classes, _order) => {
      if (!_order || !_order[0]) return classes;
      const sortedIdx = _order[0];
      const direction = _order[1];
      if (sortedIdx === colIdx) {
        classes = `dt-ordering-${direction}`;
      }
      return classes;
    }, "");
  };
  const currentOrder = state.currentOrder || props.config.order;
  const activeClass = getActiveSortClass(currentOrder, columnIdx);
  if (activeClass) {
    classes.push(activeClass);
  }
  return classes.join(" ");
};

// The export item this table last placed in the dropdown, removed before a
// rebuild attaches a fresh one so the menu never carries two.
let csvExportNodes: JQuery | null = null;

const attachCSVExport = () => {
  const { Buttons } = dataTableGlobal();
  csvExportNodes?.remove();
  const buttons = new Buttons(state.datatable, {
    dom: {
      button: {
        className: ""
      }
    },
    buttons: [
      {
        extend: "csv",
        text: "Export CSV",
        filename: props.csvFileName,
        className: "dropdown-item clickeable",
        exportOptions: {
          columns: "thead th:not(.actions-column-header)",
          format: {
            body(data) {
              // Remove HTML tags when exporting to CSV
              return data.replace(/<([^>]+)>/gi, "");
            }
          }
        }
      }
    ]
  });
  const container = $(buttons.container().get());
  container.appendTo($("#dropdown-menu-buttons"));
  csvExportNodes = container.contents().unwrap();
};

// Collapse a portal to the control the live table teleported in last.
//
// A table left mounted after a Turbo navigation still teleports its own
// controls, and Vue resolves the portal selector to the first match in the
// document, so the stale control lands in the live table's portal and the two
// stack up.
const collapsePortal = (portalId: string) => {
  const portal = document.getElementById(portalId);
  if (!portal) return;
  Array.from(portal.children)
    .slice(0, -1)
    .forEach((child) => child.remove());
};

// Runs on a timer so it sees each portal after Vue has flushed this draw's
// teleports. Both portals are collapsed independently: the page-jump control
// has its own, so keeping the last child of each leaves one of each rather
// than dropping whichever rendered first.
const cleanupPageLength = () => {
  setTimeout(() => {
    collapsePortal(`pageSelectPortal-${props.id}`);
    collapsePortal(`pageJumpPortal-${props.id}`);
  });
};

function search(value: string) {
  if (state.datatable) state.datatable.search(value).draw();
}

defineExpose({
  markAllSelectionsAcrossPages,
  refreshData,
  refreshDataWithCallback,
  exportReport,
  search
});
</script>

<style lang="scss">
.dataTables_scrollHeadInner {
  width: 100% !important;
}
.dataTables_scrollHeadInner table {
  width: 100% !important;
}
.datatable-v3 .date-picker-input {
  padding-right: 30px;
  min-width: 190px;
}

.dt-scroll-head,
.dt-scroll-body,
.dt-scroll-foot {
  overflow: visible !important;
}
/* DataTables 3 turns `scrollY: false` into `height: 100%` on its body div.
   Under the sticky header inside the fixed-height .dt-scroll that always
   overflows by one header, so the body keeps its natural height here. */
.datatable-v3 .dt-scroll-body {
  height: auto !important;
  max-height: none !important;
}
.dt-scroll-head {
  position: sticky !important;
  top: 0;
  z-index: 2;
}
.dt-scroll {
  overflow: auto;
}
.column-search-field {
  min-width: 90px;
}
div.dt-container div.row > * {
  margin-bottom: 0 !important;
}
</style>
